import React, { useState, useEffect, useRef, useCallback } from 'react';
import debounce from 'lodash.debounce';
import { connect } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { css, StyleSheet } from 'aphrodite/no-important';
import { blue, gray, salmon } from '../../theme';
import {
  Loader,
  TabsContainer,
  TabItem,
  PopupMenu,
  Checkbox,
  TextInput
} from 'quantux-ui';
// ----- Local imports
import PreOp from './preop';
import IntraOp from './intraop';
import CameraDrawer from '../doc-writer/camera-drawer';
import CamInspectorDrawer from '../doc-writer/cam-inspector-drawer';
import DocWriterDrawer from '../doc-writer/doc-writer-drawer';
import CropperDrawer from '../doc-writer/cropper-drawer';
import PatientCard from '../patient-card';
import CaseDoc from '../case-doc';
import CaseTab from './case-tab';
import PatientTab from './patient-tab';
import PayerTab from './payer-tab';
import {
  findBase64ImageMimeType,
  findFileExtensionByMimeType,
  findFlag,
  FLAG_VALUE,
  FORM_TYPE,
  BASE_URL,
  getHighlightedText,
  calculateCaseScheduleDataFlag,
  debugLogToConsole
} from '../../utils';
// --- Flags icon
import DocumentCriticalIcon from '../svg-icons/document-critical-icon';
import DocumentSuccessIcon from '../svg-icons/document-success-icon';
import DocumentNoneIcon from '../svg-icons/document-none-icon';
import DocumentWarningIcon from '../svg-icons/document-warning-icon';
import CaseSuccessIcon from '../svg-icons/case-success-icon';
import CaseWarningIcon from '../svg-icons/case-warning-icon';
import CaseCriticalIcon from '../svg-icons/case-critical-icon';
// ----- Actions
import {
  clearCaseSchedulesData,
  updateCaseSchedule,
  saveCaseSchedule,
  collapseReload,
  toggleCaseLoader,
  loadCaseSchedule,
  addPatientToExistingCaseSchedule,
  deletePatientFromCaseSchedule,
  reloadExistingCaseSchedulePatientData
} from '../../actions';
import { clone } from 'ramda';
import CaseField from '../revenue-cycle-management/case-field';
import CaseUserCard from '../revenue-cycle-management/case-user-card';

const DRAWER_WIDTH = 355;
const MOBILE_BREAK_POINT = 1012;

const COMMON_TAB_ITEM_STYLES = {
  height: '30px',
  borderBottom: 'none',
  marginRight: '0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const TABS_TEXT_STYLES = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '12px'
};

// ----- Icons ----------------------------------------------------------------
const IS_NONE_ICON = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="none"
  >
    <path
      fill="#96C8FF"
      fillRule="evenodd"
      d="M8 0C3.584 0 0 3.584 0 8s3.584 8 8 8 8-3.584 8-8-3.584-8-8-8ZM1.714 8.029c0-3.452 2.855-6.248 6.379-6.248 1.475 0 2.83.492 3.907 1.32l-8.938 8.754A6.091 6.091 0 0 1 1.714 8.03ZM4 12.904a6.385 6.385 0 0 0 3.907 1.32c3.524 0 6.379-2.796 6.379-6.247a6.091 6.091 0 0 0-1.348-3.827L4 12.904Z"
      clipRule="evenodd"
    />
  </svg>
);

const CRITIAL_ICON = (
  <svg
    width="16"
    height="13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16 12.364 8 0 0 12.364h16Zm-7.273-2.182H7.273V8.728h1.454v1.454ZM7.273 8h1.454V5.091H7.273v2.91Z"
      fill="#FF0"
    />
  </svg>
);

const BELL_ICON = (
  <svg
    width="16"
    height="16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14 7.439v4.376l2 1.75v.875H9.988C9.878 15.314 9.026 16 8 16c-1.036 0-1.88-.686-1.988-1.56H0v-.875l2-1.75V7.439c0-2.696 1.63-4.936 4.5-5.531v-.595C6.5.586 7.17 0 8 0s1.5.586 1.5 1.313v.595c2.86.595 4.5 2.844 4.5 5.53Z"
      fill="#FF6060"
    />
  </svg>
);

const SUCCESS_ICON = (
  <svg
    width="12"
    height="12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 6a6 6 0 1 1 12 0A6 6 0 1 1 0 6Zm2.646-.238 1.896 1.796L9.36 3l.641.608-5.458 5.17L2 6.369l.646-.607Z"
      fill="#00B080"
    />
  </svg>
);

// ----- Case documents tabs array --------------------------------------------
const TABS = [
  // Facesheet Tab
  {
    id: 'faceSheet_tab',
    pKey: '1111',
    isIconText: false,
    text: 'FaceSheet',
    hideOnMobile: false,
    styles: {
      ...COMMON_TAB_ITEM_STYLES,
      width: window.innerWidth > 720 ? '12.5%' : '12.5%',
      minWidth: '91px',
      maxWidth: window.innerWidth > 720 ? '12.5%' : '12.5%'
    }
  },
  // Results Tab
  {
    id: 'results_tab',
    pKey: '1331',
    isIconText: false,
    text: 'Results',
    hideOnMobile: false,
    styles: {
      ...COMMON_TAB_ITEM_STYLES,
      width: window.innerWidth > 720 ? '12.5%' : '12.5%',
      minWidth: '91px',
      maxWidth: window.innerWidth > 720 ? '12.5%' : '12.5%',
      borderLeft: '1px solid black'
    }
  },
  // Consents Tab
  {
    id: 'consents_tab',
    pKey: '1112',
    isIconText: false,
    text: 'Consents',
    hideOnMobile: false,
    styles: {
      ...COMMON_TAB_ITEM_STYLES,
      width: window.innerWidth > 720 ? '12.5%' : '12.5%',
      minWidth: '91px',
      maxWidth: window.innerWidth > 720 ? '12.5%' : '12.5%',
      borderLeft: '1px solid black'
    }
  },
  // Anesthesia Tab
  {
    id: 'anesthesia_tab',
    pKey: '1115',
    isIconText: false,
    text: 'Anesthesia',
    hideOnMobile: false,
    styles: {
      ...COMMON_TAB_ITEM_STYLES,
      width: window.innerWidth > 720 ? '12.5%' : '12.5%',
      minWidth: '91px',
      maxWidth: window.innerWidth > 720 ? '12.5%' : '12.5%',
      borderLeft: '1px solid black'
    }
  },
  // Surgeon Tab
  {
    id: 'surgeon_tab',
    pKey: '1114',
    isIconText: false,
    text: 'Surgeon',
    hideOnMobile: true,
    styles: {
      ...COMMON_TAB_ITEM_STYLES,
      width: '12.5%',
      minWidth: '91px',
      maxWidth: '12.5%',
      borderLeft: '1px solid black'
    }
  },
  // Nursing Tab
  {
    id: 'nursing_tab',
    pKey: '1113',
    isIconText: false,
    text: 'Nursing',
    hideOnMobile: true,
    styles: {
      ...COMMON_TAB_ITEM_STYLES,
      width: '12.5%',
      minWidth: '91px',
      maxWidth: '12.5%',
      borderLeft: '1px solid black'
    }
  },
  // Orders Tab
  {
    id: 'orders_tab',
    pKey: '8899',
    isIconText: false,
    text: 'Orders',
    hideOnMobile: true,
    styles: {
      ...COMMON_TAB_ITEM_STYLES,
      width: '12.5%',
      minWidth: '91px',
      maxWidth: '12.5%',
      borderLeft: '1px solid black'
    }
  },
  // Disccharge Tab
  {
    id: 'discharge_tab',
    pKey: '1116',
    isIconText: false,
    text: 'Discharge',
    hideOnMobile: true,
    styles: {
      ...COMMON_TAB_ITEM_STYLES,
      width: '12.5%',
      minWidth: '91px',
      maxWidth: '12.5%',
      borderLeft: '1px solid black'
    }
  }
];

const ScheduleCaseDashboard = (props) => {
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  // ----- Flags
  const [scheduleMasterFlag, setScheduleMasterFlag] = useState(FLAG_VALUE.critical);
  const [patientFlag, setPatientFlag] = useState(FLAG_VALUE.warning);
  const [payerFlag, setPayerFlag] = useState(FLAG_VALUE.warning);
  // ----- Data
  const [caseData, setCaseData] = useState({});
  window.cd = caseData;
  const [patientData, setPatientData] = useState({});
  // ----- UI
  const [isMobileLayout, setIsMobileLayout] = useState(window.innerWidth < MOBILE_BREAK_POINT);
  const [loaderMsg, setLoaderMsg] = useState('Saving Case Data');
  const [showLoader, setShowLoader] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showDocWriter, setShowDocWriter] = useState(false);
  const [docWriterData, setDocWriterData] = useState({});
  // --- Tabs
  const [activeTab, setActiveTab] = useState(TABS[0].text);
  const [consentActiveTab, setConsentActiveTab] = useState('anesthesia');
  const [anesthesiaSubTab, setAnesthesiaSubTab] = useState('preOp');
  const [recordsSubTab, setRecordsSubTab] = useState('labs');
  const [ordersSubTab, setOrdersSubTab] = useState('preOp');
  const [surgeonSubTab, setSurgeonSubTab] = useState('preOp');
  const [nursingSubTab, setNursingSubTab] = useState('preOp');
  // -- Case data drawer
  const [showCaseDataDrawer, setShowCaseDataDrawer] = useState(false);
  const [caseDataDrawerMasterFlag, setCaseDataDrawerMasterFlag] = useState(FLAG_VALUE.critical);
  const [caseDataDrawerActiveTab, setCaseDataDrawerActiveTab] = useState('case');
  // -- Scan document (Camera and Cam Inspector drawers)
  const [showCameraDrawer, setShowCameraDrawer] = useState(false);
  const [showCamInspectorDrawer, setShowCamInspectorDrawer] = useState(false);
  const [scanDocumentData, setScanDocumentData] = useState({});
  const [showCropperDrawer, setShowCropperDrawer] = useState(false);
  const [scannedDocs, setScannedDocs] = useState({});
  const [cropperDocs, setCropperDocs] = useState({});
  // -- CameraDrawer and CamInspectorDrawer for page 2 and onwards
  const [showPrvCamDrawer, setShowPrvCamDrawer] = useState(false);
  const [showPrvCamInspectorDrawer, setShowPrvCamInspectorDrawer] = useState(false);

  // -- Anesthesia procedure search
  const [anesthesiaProcedureValue, setAnesthesiaProcedureValue] = useState('');
  const [showAnesthesiaProceduresSearchMenu, setShowAnesthesiaProceduresSearchMenu] = useState(false);
  const [anesthesiaProceduresSearchResults, setAnesthesiaProceduresSearchResults] = useState([]);

  // ----- URL params & history object ----------------------------------------
  const { scheduleId } = useParams();
  const history = useHistory();

  // ----- Props -------------------------------------------------------------
  const {
    caseSchedules = [],
    activeCaseSchedule: acs,
    activeCaseSchedulePatientData: apd,
  } = props;

  // ----- Debug --------------------------------------------------------------
  window.cdocs = caseData;

  // ----- Refs ---------------------------------------------------------------
  const headerMenuRef = useRef();
  const procedureSearchMenuRef = useRef();
  const docWriterWrapperRef = useRef();

  // ___ NETWORK OPS __________________________________________________________
  const searchSurgicalProcedures = (value) => {
    const url = new URL(`${BASE_URL}/anesProc/searchAnesthesiaProcedures`);
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setShowAnesthesiaProceduresSearchMenu(true);
        setAnesthesiaProceduresSearchResults(data.anesthesiaProcedures);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedProceduresSearch = useCallback(debounce(value => searchSurgicalProcedures(value), 800), []);

  // ----- Effects ------------------------------------------------------------

  // Setting window resize and orientation change event handlers
  useEffect(() => {
    const handleWindowResize = () => {
      setWindowHeight(window.innerHeight);
      setWindowWidth(window.innerWidth);
    };

    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('orientationchange', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('orientationchange', handleWindowResize);
    };
  }, []);

  // Loading initial case and patient data
  useEffect(() => {
    if (caseSchedules.length > 0) {
      props.toggleCaseLoader(true);
      props.loadCaseSchedule(scheduleId);
    }
  }, [caseSchedules]);

  // ---- Case, patient and payers data and flags
  useEffect(() => {
    console.log('ACS effect --- fired!!!');
    if (Object.keys(acs).length > 0) {
      console.log('ACS effect --- running!!!');
      const {
        firstName: patientFirstName = '',
        middleName: patientMiddleName = '',
        lastName: patientLastName = '',
        gender: patientGender = '',
        dob: patientDob = '',
        isNoPrimaryInsurance = false,
        primaryInsurance = {},
        isNoSecondaryInsurance = false,
        secondaryInsurance = {},
        isNoTertiaryInsurance = false,
        tertiaryInsurance = {},
        isNoSelfPay = false
      } = apd;

      const {
        patientType = '',
        surgeons = [],
        preOpDx: admDx = [],
        surgicalProcedures = [],
        otherSurgicalProcedures = [],
        anesthesiologists = [],
        // For docs flags calculation
        isNoneFaceSheet = false,
        faceSheetDocs = [],
        labsDocs = [],
        isNoneLabDocs = false,
        ekgDocs = [],
        isNoneEkgDocs = false,
        imagingDocs = [],
        isNoneImagingDocs = false,
        consultsDocs = [],
        isNoneConsults = false,
        consentSurgFormDocs = [],
        isNoneConsentSurgFormDocs = false,
        consentFormDocs = [],
        isNoneConsentFormDocs = false,
        preOpFormDocs = [],
        isNonePreOpFormDocs = false,
        asaScore = '',
        anesthesiaTypes = [],
        anesthesiaModifiers = [],
        isNoneIntraOpFormDocs = false,
        intraOpFormDocs = [],
        postOpFormDocs = [],
        isNonePostOpFormDocs = false,
        isNoneAnesthesiaProcedure = false,
        anesthesiaProcedures = [],
        nursingDocs = [],
        isNoneNursingDocs = false,
        surgeonDocs = [],
        isNoneSurgeonDocs = false,
        dischargeDocs = [],
        isNoneDischargeDocs = false,
        isNoneOrdersPreOpDocs = false,
        ordersPreOpDocs = [],
        isNoneOrdersIntraOpDocs = false,
        ordersIntraOpDocs = [],
        isNoneOrdersPostOpDocs = false,
        ordresPostOpDocs = [],
        anesthesiaStartDate,
        anesthesiaEndDate,
        surgeryStartDate,
        surgeryEndDate,
        anesthesiaEndHour: aeHour,
        anesthesiaEndMinute: aeMinute,
        anesthesiaStartHour: asHour,
        anesthesiaStartMinute: asMinute,
        surgeryEndHour: seHour,
        surgeryEndMinute: seMinute,
        surgeryStartHour: ssHour,
        surgeryStartMinute: ssMinute,
      } = acs;

      const facilityName = acs.facility ? acs.facility.facilityName : '';

      // --- Case data
      setCaseData(acs);

      // --- Patient & payers data and flags
      setPatientData({
        dos: acs.scheduleStartDate || '',
        patientType,
        facilityName,
        surgeons,
        admDx,
        surgicalProcedures,
        otherSurgicalProcedures,
        anesthesia: anesthesiologists,
        firstName: patientFirstName,
        middleName: patientMiddleName,
        lastName: patientLastName,
        gender: patientGender,
        dob: patientDob
      });

      // --- Payer flag - Calculated using primary, secondary, tertiary and self-pay options
      let piFlag = FLAG_VALUE.warning;
      let siFlag = FLAG_VALUE.warning;
      let tiFlag = FLAG_VALUE.warning;
      const spFlag = isNoSelfPay ? FLAG_VALUE.success : FLAG_VALUE.warning;

      // Primary insurance flag
      if (isNoPrimaryInsurance) {
        piFlag = FLAG_VALUE.success
      } else {
        piFlag = Object.keys(primaryInsurance).length > 0
          ? FLAG_VALUE.success
          : FLAG_VALUE.warning;
      }

      // Secondary insurance flag
      if (isNoSecondaryInsurance) {
        siFlag = FLAG_VALUE.success;
      } else {
        siFlag = Object.keys(secondaryInsurance).length > 0
          ? FLAG_VALUE.success
          : FLAG_VALUE.warning
      }

      // Tertiary insurance flag
      if (isNoTertiaryInsurance) {
        tiFlag = FLAG_VALUE.success;
      } else {
        tiFlag = Object.keys(tertiaryInsurance).length > 0
          ? FLAG_VALUE.success
          : FLAG_VALUE.warning;
      }

      // Payer flag
      const payerFlagValue = findFlag(piFlag, siFlag, tiFlag, spFlag);
      setPayerFlag(payerFlagValue);

      // Patient Flag
      const patientFlagValue = Object.keys(apd).length > 0 ? FLAG_VALUE.success : FLAG_VALUE.warning;
      setPatientFlag(patientFlagValue);

      /* ----- Case schedule master tab calcualtion ----- */
      // --- FaceSheet tab flag
      let faceSheetFlag = FLAG_VALUE.critical;
      if (isNoneFaceSheet) {
        faceSheetFlag = FLAG_VALUE.none;
      } else if (faceSheetDocs.length > 0 || isNoneFaceSheet) {
        faceSheetFlag = FLAG_VALUE.success;
      } else {
        faceSheetFlag = FLAG_VALUE.critical;
      }

      // --- Records tab flag
      let recordsFlag = FLAG_VALUE.critical;
      if (isNoneLabDocs && isNoneEkgDocs && isNoneImagingDocs && isNoneConsults) {
        recordsFlag = FLAG_VALUE.none;
      } else {
        const labsFlag = (Array.isArray(labsDocs) && labsDocs.length > 0) || isNoneLabDocs;
        const ekgFlag = (Array.isArray(ekgDocs) && ekgDocs.length > 0) || isNoneEkgDocs;
        const imagingFlag = (Array.isArray(imagingDocs) && imagingDocs.length > 0) || isNoneImagingDocs;
        const consultsFlag = (Array.isArray(consultsDocs) && consultsDocs.length > 0) || isNoneConsults;

        if (labsFlag && ekgFlag && imagingFlag && consultsFlag) {
          recordsFlag = FLAG_VALUE.success;
        } else {
          recordsFlag = FLAG_VALUE.critical;
        }
      }

      // --- Consent tab flag
      let consentFlag = FLAG_VALUE.critical;
      if (isNoneConsentSurgFormDocs && isNoneConsentFormDocs) {
        consentFlag = FLAG_VALUE.none;
      } else {
        const surgFlag = consentSurgFormDocs.length > 0 || isNoneConsentSurgFormDocs;
        const anesFlag = consentFormDocs.length > 0 || isNoneConsentFormDocs;

        if (surgFlag && anesFlag) {
          consentFlag = FLAG_VALUE.success;
        } else {
          consentFlag = FLAG_VALUE.critical;
        }
      }

      // --- Anesthesia  tab flag
      let anesthesiaFlag = FLAG_VALUE.critical;
      // PreOp sub-tab flag
      let preOpFlagValue = FLAG_VALUE.critical;
      if (isNonePreOpFormDocs) {
        preOpFlagValue = FLAG_VALUE.none;
      } else {
        if (preOpFormDocs.length > 0) {
          preOpFlagValue = FLAG_VALUE.success;
        } else {
          preOpFlagValue = FLAG_VALUE.critical;
        }
      }
      // IntraOp sub-tab flag
      let intraOpFlagValue = FLAG_VALUE.critical;
      let anesthesiaDataFlagValue = FLAG_VALUE.critical;
      if (asaScore.length > 0 && anesthesiaTypes.length > 0 && anesthesiaModifiers.length > 0) {
        anesthesiaDataFlagValue = FLAG_VALUE.success;
      }
      if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
        intraOpFlagValue = anesthesiaDataFlagValue;
      } else {
        if (isNoneIntraOpFormDocs) {
          intraOpFlagValue = FLAG_VALUE.none;
        } else {
          if (intraOpFormDocs.length > 0) {
            intraOpFlagValue = FLAG_VALUE.success;
          } else {
            intraOpFlagValue = FLAG_VALUE.critical;
          }
        }
      }
      // PostOp sub-tab flag
      let postOpFlagValue = FLAG_VALUE.critical;
      if (isNonePostOpFormDocs) {
        postOpFlagValue = FLAG_VALUE.none;
      } else {
        if (postOpFormDocs.length > 0) {
          postOpFlagValue = FLAG_VALUE.success;
        } else {
          postOpFlagValue = FLAG_VALUE.critical;
        }
      }
      // Procedures sub-tab flag
      let proceduresFlagValue = FLAG_VALUE.critical;
      if (isNoneAnesthesiaProcedure) {
        proceduresFlagValue = FLAG_VALUE.none;
      } else {
        if (anesthesiaProcedures.length === 0) {
          proceduresFlagValue = FLAG_VALUE.critical;
        } else if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
          proceduresFlagValue = FLAG_VALUE.warning;
        } else if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length === 0) {
          proceduresFlagValue = FLAG_VALUE.success;
        } else {
          proceduresFlagValue = FLAG_VALUE.critical;
        }
      }
      // Anesthesia main tab flag calculation
      const anesthesiaTabFlag = findFlag(preOpFlagValue, intraOpFlagValue, postOpFlagValue, proceduresFlagValue);
      switch (anesthesiaTabFlag) {
        case FLAG_VALUE.critical:
          anesthesiaFlag = FLAG_VALUE.critical;
          break;
        case FLAG_VALUE.warning:
          anesthesiaFlag = FLAG_VALUE.warning;
          break;
        case FLAG_VALUE.success:
          anesthesiaFlag = FLAG_VALUE.success;
          break;
        case FLAG_VALUE.none:
          anesthesiaFlag = FLAG_VALUE.none;
          break;
        default:
          anesthesiaFlag = FLAG_VALUE.critical;
      }

      // --- Nursing tab flag
      let nursingFlag = FLAG_VALUE.critical;
      if (isNoneNursingDocs) {
        nursingFlag = FLAG_VALUE.none;
      } else {
        if (nursingDocs.length > 0 || isNoneNursingDocs) {
          nursingFlag = FLAG_VALUE.success;
        } else {
          nursingFlag = FLAG_VALUE.critical;
        }
      }

      // -- Surgeon tab flag
      let surgeonFlag = FLAG_VALUE.critical;
      if (isNoneSurgeonDocs) {
        surgeonFlag = FLAG_VALUE.none;
      } else {
        if (surgeonDocs.length > 0 || isNoneSurgeonDocs) {
          surgeonFlag = FLAG_VALUE.success;
        } else {
          surgeonFlag = FLAG_VALUE.critical;
        }
      }

      // --- Orders tab flag
      let ordersFlag = FLAG_VALUE.critical;
      if (isNoneOrdersPreOpDocs && isNoneOrdersIntraOpDocs && isNoneOrdersPostOpDocs) {
        ordersFlag = FLAG_VALUE.none;
      } else {
        const preOPFlag = ordersPreOpDocs.length > 0 || isNoneOrdersPreOpDocs;
        const intraOpFlag = ordersIntraOpDocs.length > 0 || isNoneOrdersIntraOpDocs;
        const postOpFlag = ordresPostOpDocs.length > 0 || isNoneOrdersPostOpDocs;

        if (preOPFlag && intraOpFlag && postOpFlag) {
          ordersFlag = FLAG_VALUE.success;
        } else {
          ordersFlag = FLAG_VALUE.critical;
        }
      }

      // ----- Discharge tab flag
      let dcFlag = FLAG_VALUE.critical;
      if (isNoneDischargeDocs) {
        dcFlag = FLAG_VALUE.none;
      } else {
        if (dischargeDocs.length > 0 || isNoneDischargeDocs) {
          dcFlag = FLAG_VALUE.success;
        } else {
          dcFlag = FLAG_VALUE.critical;
        }
      }

      // ----- Case times flag
      const anesStart = new Date(anesthesiaStartDate);
      const asDate = anesStart.getDate();
      const asMonth = anesStart.getMonth();
      const asYear = anesStart.getFullYear();
      const isAnesStartTimePristine = asHour === undefined || asMinute === undefined;

      const anesEnd = new Date(anesthesiaEndDate);
      const aeDate = anesEnd.getDate();
      const aeMonth = anesEnd.getMonth();
      const aeYear = anesEnd.getFullYear();

      const surgStart = new Date(surgeryStartDate);
      const ssDate = surgStart.getDate();
      const ssMonth = surgStart.getMonth();
      const ssYear = surgStart.getFullYear();

      const surgEnd = new Date(surgeryEndDate);
      const seDate = surgEnd.getDate();
      const seMonth = surgEnd.getMonth();
      const seYear = surgEnd.getFullYear();

      const anesStartDateTimeInseconds = new Date(asYear, asMonth, asDate, asHour, asMinute).getTime();
      const anesEndDateTimeInseconds = new Date(aeYear, aeMonth, aeDate, aeHour, aeMinute).getTime();
      const surgStartDateTimeInSeconds = new Date(ssYear, ssMonth, ssDate, ssHour, ssMinute).getTime();
      const surgEndDateTimeInSeconds = new Date(seYear, seMonth, seDate, seHour, seMinute).getTime();
      const anesStartFlag = isAnesStartTimePristine ? FLAG_VALUE.critical : FLAG_VALUE.success;
      const surgStartFlag = surgStartDateTimeInSeconds - anesStartDateTimeInseconds > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      const surgEndFlag = surgEndDateTimeInSeconds - surgStartDateTimeInSeconds > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      const anesEndFlag = anesEndDateTimeInseconds - surgEndDateTimeInSeconds > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
      const timeFlag = findFlag(anesStartFlag, surgStartFlag, surgEndFlag, anesEndFlag);
      // --- Debugging ---
      console.log('⏰ Case times flags ⏰');
      console.log(`asDate = ${asDate}`);
      console.log(`asMonth = ${asMonth}`);
      console.log(`asYear = ${asYear}`);
      console.log(`asHour = ${asHour}`);
      console.log(`asMinute = ${asMinute}`);
      console.log(`isAnesStartTimePristine = ${isAnesStartTimePristine}`);
      console.log('--------------------------');
      console.log(`aeDate = ${aeDate}`);
      console.log(`aeMonth = ${aeMonth}`);
      console.log(`aeYear = ${aeYear}`);
      console.log(`aeHour = ${aeHour}`);
      console.log(`aeMinute = ${aeMinute}`);
      console.log('--------------------------');
      console.log(`ssDate = ${ssDate}`);
      console.log(`ssMonth = ${ssMonth}`);
      console.log(`ssYear = ${ssYear}`);
      console.log(`ssHour = ${ssHour}`);
      console.log(`ssMinute = ${ssMinute}`);
      console.log('--------------------------');
      console.log(`seDate = ${seDate}`);
      console.log(`seMonth = ${seMonth}`);
      console.log(`seYear = ${seYear}`);
      console.log(`seHour = ${seHour}`);
      console.log(`seMinute = ${seMinute}`);
      console.log('-------- Case Drawer Flags --------');
      console.log(`props.caseTabFlag = ${props.caseTabFlag}`);
      console.log(`patientFlagValue = ${patientFlagValue}`);
      console.log(`payerFlagValue = ${payerFlagValue}`);
      console.log('-------- Flag Values --------');
      console.log(`anesStartFlag = ${anesStartFlag}`);
      console.log(`surgStartFlag = ${surgStartFlag}`);
      console.log(`surgEndFlag = ${surgEndFlag}`);
      console.log(`anesEndFlag = ${anesEndFlag}`);

      // ----- Case schedule master flag
      const caseDrawerFlag = findFlag(props.caseTabFlag, patientFlagValue, payerFlagValue);
      const tmpMasterFlag = findFlag(
        caseDrawerFlag,
        timeFlag,
        faceSheetFlag,
        recordsFlag,
        consentFlag,
        nursingFlag,
        anesthesiaFlag,
        ordersFlag,
        dcFlag
      );
      console.log('');
      console.log('------ 💡💡 Case Schedule Master Flag 💡💡 ------');
      console.log(`caseDrawerFlag = ${caseDrawerFlag}`);
      console.log(`timeFlag = ${timeFlag}`);
      console.log(`faceSheetFlag = ${faceSheetFlag}`);
      console.log(`recordsFlag = ${recordsFlag}`);
      console.log(`consentFlag = ${consentFlag}`);
      console.log(`nursingFlag = ${nursingFlag}`);
      console.log(`anesthesiaFlag = ${anesthesiaFlag}`);
      console.log(`ordersFlag = ${ordersFlag}`);
      console.log(`dcFlag = ${dcFlag}`);
      console.log(`Case schedule master flag = ${tmpMasterFlag}`);
      console.log('');
      console.log('');
      setScheduleMasterFlag(tmpMasterFlag);
    }
  }, [
    acs,
    apd,
    caseDataDrawerMasterFlag,
    props.caseTabFlag
  ]);

  // --- Anesthesia procedures search
  useEffect(() => {
    if (anesthesiaProcedureValue && anesthesiaProcedureValue.length === 0) {
      setAnesthesiaProceduresSearchResults([]);
    }
    if (anesthesiaProcedureValue && anesthesiaProcedureValue.trim().length > 0) {
      debouncedProceduresSearch(anesthesiaProcedureValue);
    }
  }, [anesthesiaProcedureValue]);

  // Schedule drawer ** Case Tab Master Flag **
  useEffect(() => {
    setCaseDataDrawerMasterFlag(findFlag(props.caseTabFlag, patientFlag, payerFlag));
  }, [
    props.caseTabFlag,
    patientFlag,
    payerFlag
  ]);

  // ----- Component Styles ---------------------------------------------------
  const styles = StyleSheet.create({
    scheduleDashboardContainer: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: blue[900],
      zIndex: '5000'
    },
    dashboardHeader: {
      height: '40px',
      background: blue[700],
      borderBottom: '1px solid #000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 4px'
    },
    dashboardHeaderTitle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      fontWeight: 'bold',
      lineHeight: '16px'
    },
    dashboardContainer: {
      display: 'flex',
      height: `${windowHeight - 40}px`,
      overflow: 'hidden'
    },
    caseDocsContainer: {
      position: 'relative',
      display: 'flex',
      flexFlow: 'column',
      width: showCaseDataDrawer ? 'calc(100vw - 355px)' : '100vw',
      maxWidth: showCaseDataDrawer ? 'calc(100vw - 355px)' : '100vw'
    },
    patientStickerContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingRight: '4px',
      height: '141px',
      minHeight: '141px',
      background: blue[900],
      flexGrow: '0'
    },
    docTabs: {
      background: 'black',
      padding: '1px 0',
      flexGrow: '0'
    },
    docContainer: {
      height: 'calc(100% - 174px)'
    },
    searchPopupMenuContainer: {
      width: `${DRAWER_WIDTH - 21}px`,
      background: blue[900],
      paddingBottom: '1px',
      borderRadius: '5px',
      overflowY: 'auto',
      maxHeight: '480px'
    },
    headerEllipsisBtn: {
      position: 'relative',
      width: '24px',
      height: '24px',
      background: blue[200],
      color: gray[600],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '2px'
    },
    menuItem: {
      cursor: 'pointer',
      background: gray[900],
      color: gray[300],
      padding: '4px',
      lineHeight: '16px',
      fontSize: '14px',
      marginTop: '1px',
      ':hover': {
        background: '#184878',
        color: 'white'
      },
      ':last-child': {
        borderBottom: '0'
      }
    },
    suggResults: {
      background: gray[900],
      color: blue[500],
      height: '32px'
    },
    caseDataDrawerContainer: {
      width: '355px',
      borderLeft: `${showCaseDataDrawer ? '1px' : '0'} solid ${blue[400]}`,
      background: gray[700],
      position: isMobileLayout ? 'absolute' : 'relative',
      top: '0px'
    },
    caseDataDrawerContainerMobile: {
      top: '40px',
      right: '0',
      height: 'calc(100vh - 40px)'
    },
    caseDataDrawerContainerClosed: {
      width: '0',
      display: isMobileLayout ? 'none' : ''
    },
    caseDataDrawerHeader: {
      display: 'flex',
      alignItems: 'center',
      height: '40px',
      backgroundColor: blue[700],
      padding: '0 8px'
    },
    caseDataDrawerHeaderTitle: {
      flexGrow: '1'
    },
    caseDataDrawerTabs: {
      maxHeight: 'calc(100% - 56px)',
      overflowY: 'auto',
      overflowX: 'hidden'
    },
    caseDataDrawerActiveComponentContainer: {
      height: 'calc(100% - 71px)',
      overflowY: 'auto'
    },
    caseDataDrawerHeaderCloseIcon: {
      flexGrow: '0',
      ':hover': {
        color: salmon[500]
      }
    },
    pdfDocContainer: {
      width: '100%',
      height: 'calc(100% - 32px)'
    },
    anesthesisProcedureTabContainer: {
      width: `${DRAWER_WIDTH}px`
    }
  });

  // ----- Patient Sticker
  const handleOnClickOpenCaseDataDrawer = () => {
    setShowCaseDataDrawer(true);
  };

  // --- Docs common event handlers
  const handleOnEditDocs = (data) => {
    const {
      scheduleId,
      currentPageId,
      formType,
      formName,
      caseDocuments
    } = data;

    setDocWriterData({
      scheduleId,
      currentPageId,
      formType,
      formName,
      caseDocuments
    });
    setShowDocWriter(true);
  };

  const handleOnScanDocs = (data) => {
    setScanDocumentData(clone(data));
    setShowCameraDrawer(true);
  };

  // Common case docs cropper handler
  const handleOnShowCropper = (data) => {
    setCropperDocs(clone(data));
    setShowCropperDrawer(true);
  };

  // Common case docs camera drawer handler
  const handleOnShowCamera = (data) => {
    console.log(data);
    setScannedDocs(clone(data));
    setShowPrvCamDrawer(true);
  };

  const handleOnShowDocWriter = (data) => {
    console.log(data);
    const {
      scheduleId,
      currentPageId,
      formType,
      formName,
      caseDocuments
    } = data;

    setDocWriterData({
      scheduleId,
      currentPageId,
      formType,
      formName,
      caseDocuments
    });
    setShowDocWriter(true);
    setScannedDocs(clone(data));
  };

  // -- Common cropper drawer event handler
  const handleOnCropperClose = (docs) => {
    const updatedDocs = clone(docs);

    switch (cropperDocs.formType) {
      case FORM_TYPE.faceSheet:
        props.updateCaseSchedule({ faceSheetDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          faceSheetDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsLabs:
        props.updateCaseSchedule({ labsDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          labsDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsEkg:
        props.updateCaseSchedule({ ekgDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ekgDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsImaging:
        props.updateCaseSchedule({ imagingDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          imagingDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsConsults:
        props.updateCaseSchedule({ consultsDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          consultsDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.consentsSurgery:
        props.updateCaseSchedule({ consentSurgFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          consentSurgFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.consentsAnesthesia:
        props.updateCaseSchedule({ consentFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          consentFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.nursingPreOp:
        props.updateCaseSchedule({ nursingPreOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          nursingPreOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.nursingIntraOp:
        props.updateCaseSchedule({ nursingIntraOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          nursingIntraOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.nursingPostOp:
        props.updateCaseSchedule({ nursingPostOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          nursingPostOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.surgeonPreOp:
        props.updateCaseSchedule({ surgeonPreOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          surgeonPreOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.surgeonIntraOp:
        props.updateCaseSchedule({ surgeonIntraOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          surgeonIntraOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.surgeonPostOp:
        props.updateCaseSchedule({ surgeonPostOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          surgeonPostOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.anesthesiaPreOp:
        props.updateCaseSchedule({ preOpFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          preOpFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.anesthesiaIntraOp:
        props.updateCaseSchedule({ intraOpFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          intraOpFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.anesthesiaPostOp:
        props.updateCaseSchedule({ postOpFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          postOpFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.ordersPreOp:
        props.updateCaseSchedule({ ordersPreOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ordersPreOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.ordersIntraOp:
        props.updateCaseSchedule({ ordersIntraOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ordersIntraOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.ordersPostOp:
        props.updateCaseSchedule({ ordresPostOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ordresPostOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.discharge:
        props.updateCaseSchedule({ dischargeDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          dischargeDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      default:
        console.log(`Not implemented for ${cropperDocs.formType}`);
    }

    setShowCropperDrawer(false);
    setCropperDocs({});
  };

  // -- Common Prv CamDrawer event handlers
  const handleOnClosePrvCameraDrawer = () => {
    setScannedDocs({});
    setShowPrvCamDrawer(false);
    setShowPrvCamInspectorDrawer(false);
  }

  const handleOnScanPrvCameraDrawer = (imageDataUrl) => {
    const imageExtension = findFileExtensionByMimeType(findBase64ImageMimeType(imageDataUrl));
    let updatedCaseDocs = clone(scannedDocs.caseDocs);
    const newDocId = Math.random().toString(36).slice(2);
    const newFileName = `file_${Math.random().toString(36).slice(2)}`;

    updatedCaseDocs = updatedCaseDocs.concat({
      id: newDocId,
      imageUrl: '',
      fileName: `${newFileName}${imageExtension}`,
      compositeImage: imageDataUrl
    });

    // Update scan document data object
    setScannedDocs((prvState) => {
      return {
        ...prvState,
        caseDocs: updatedCaseDocs,
        selectedPageId: newDocId,
        newImageUrl: imageDataUrl
      };
    });

    // Hide camera drawer and show CamInspectorDrawer
    setShowPrvCamDrawer(false);
    setShowPrvCamInspectorDrawer(true);
  };

  const handleOnCancelPrvScanDocs = () => {
    setShowPrvCamDrawer(false);
    setShowPrvCamInspectorDrawer(false);
    setScannedDocs({});
  };

  const handleOnRetakePrvScanDocs = () => {
    switch (scannedDocs.formType) {
      case FORM_TYPE.faceSheet:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.faceSheetDocs)
        }));
        break;
      case FORM_TYPE.recordsLabs:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.labsDocs)
        }));
        break;
      case FORM_TYPE.recordsEkg:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.ekgDocs)
        }));
        break;
      case FORM_TYPE.recordsImaging:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.imagingDocs)
        }));
        break;
      case FORM_TYPE.recordsConsults:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.consultsDocs)
        }));
        break;
      case FORM_TYPE.consentsSurgery:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.consentSurgFormDocs)
        }));
        break;
      case FORM_TYPE.consentsAnesthesia:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.consentFormDocs)
        }));
        break;
      case FORM_TYPE.nursingPreOp:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.nursingPreOpDocs)
        }));
        break;
      case FORM_TYPE.nursingIntraOp:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.nursingIntraOpDocs)
        }));
        break;
      case FORM_TYPE.nursingPostOp:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.nursingPostOpDocs)
        }));
        break;
      case FORM_TYPE.surgeonPreOp:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.surgeonPreOpDocs)
        }));
        break;
      case FORM_TYPE.surgeonIntraOp:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.surgeonIntraOpDocs)
        }));
        break;
      case FORM_TYPE.surgeonPostOp:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.surgeonPostOpDocs)
        }));
        break;
      case FORM_TYPE.anesthesiaPreOp:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.preOpFormDocs)
        }));
        break;
      case FORM_TYPE.anesthesiaIntraOp:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.intraOpFormDocs)
        }));
        break;
      case FORM_TYPE.anesthesiaPostOp:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.postOpFormDocs)
        }));
        break;
      case FORM_TYPE.ordersPreOp:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.ordersPreOpDocs)
        }));
        break;
      case FORM_TYPE.ordersIntraOp:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.ordersIntraOpDocs)
        }));
        break;
      case FORM_TYPE.ordersPostOp:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.ordresPostOpDocs)
        }));
        break;
      case FORM_TYPE.discharge:
        setScannedDocs((prvState) => ({
          ...prvState,
          caseDocs: clone(props.activeCaseSchedule.dischargeDocs)
        }));
        break;
      default:
        console.log(`Not implemented for ${scannedDocs.formType}`);
    }
    setShowPrvCamDrawer(true);
    setShowPrvCamInspectorDrawer(false);
  };

  const handleOnClosePrvScanDocs = () => {
    const updatedDocs = clone(scannedDocs.caseDocs);

    switch (scannedDocs.formType) {
      case FORM_TYPE.faceSheet:
        props.updateCaseSchedule({ faceSheetDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          faceSheetDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsLabs:
        props.updateCaseSchedule({ labsDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          labsDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsEkg:
        props.updateCaseSchedule({ ekgDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ekgDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsImaging:
        props.updateCaseSchedule({ imagingDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          imagingDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsConsults:
        props.updateCaseSchedule({ consultsDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          consultsDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.consentsSurgery:
        props.updateCaseSchedule({ consentSurgFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          consentSurgFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.consentsAnesthesia:
        props.updateCaseSchedule({ consentFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          consentFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.nursingPreOp:
        props.updateCaseSchedule({ nursingpreOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          nursingpreOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.nursingIntraOp:
        props.updateCaseSchedule({ nursingIntraOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          nursingIntraOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.nursingPostOp:
        props.updateCaseSchedule({ nursingPostOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          nursingPostOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.surgeonPreOp:
        props.updateCaseSchedule({ surgeonPreOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          surgeonPreOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.surgeonIntraOp:
        props.updateCaseSchedule({ surgeonIntraOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          surgeonIntraOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.surgeonPostOp:
        props.updateCaseSchedule({ surgeonPostOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          surgeonPostOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.anesthesiaPreOp:
        props.updateCaseSchedule({ preOpFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          preOpFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.anesthesiaIntraOp:
        props.updateCaseSchedule({ intraOpFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          intraOpFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.anesthesiaPostOp:
        props.updateCaseSchedule({ postOpFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          postOpFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.ordersPreOp:
        props.updateCaseSchedule({ ordersPreOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ordersPreOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.ordersIntraOp:
        props.updateCaseSchedule({ ordersIntraOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ordersIntraOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.ordersPostOp:
        props.updateCaseSchedule({ ordresPostOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ordresPostOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.discharge:
        props.updateCaseSchedule({ dischargeDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          dischargeDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      default:
        console.log(`Not implemented for ${cropperDocs.formType}`);
    }
    setShowPrvCamDrawer(false);
    setShowPrvCamInspectorDrawer(false);
    setScannedDocs({});
  };

  // ----- Facesheet docs event handlers
  const handleOnDeleteFaceSheet = () => {
    props.updateCaseSchedule({ faceSheetDocs: [] });
  }

  const handleOnDeleteFacesheetPage = (pageId) => {
    let updatedFacesheetDocs = clone(caseData.faceSheetDocs);
    updatedFacesheetDocs = updatedFacesheetDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ faceSheetDocs: updatedFacesheetDocs });
  };

  const handleOnChangeIsNoneFaceSheet = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneFaceSheet: isNoneValue });
  };

  const handleOnFacesheetDocsUploaded = (updatedDocs) => {
    // Update activeCaseSchedule state var and save data
    props.updateCaseSchedule({ faceSheetDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      faceSheetDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // --- Records labs event handlers
  const handleOnDeleteRecordsLabs = () => {
    props.updateCaseSchedule({ labsDocs: [] });
  }

  const handleOnDeleteRecordsLabsPage = (pageId) => {
    let updatedLabsDocs = clone(caseData.labsDocs);
    updatedLabsDocs = updatedLabsDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ labsDocs: updatedLabsDocs });
  };

  const handleOnChangeIsNoneLabsDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneLabDocs: isNoneValue });
  };

  const handleOnRecordsLabsDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ labsDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      labsDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // --- Records EKG docs event handlers
  const handleOnDeleteRecordsEkg = () => {
    props.updateCaseSchedule({ ekgDocs: [] });
  }

  const handleOnDeleteRecordsEkgPage = (pageId) => {
    let updatedEkgDocs = clone(caseData.ekgDocs);
    updatedEkgDocs = updatedEkgDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ ekgDocs: updatedEkgDocs });
  };

  const handleOnChangeIsNoneEkgDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneEkgDocs: isNoneValue });
  };

  const handleOnRecordsEkgDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ ekgDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      ekgDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // --- Records imaging docs event handlers
  const handleOnDeleteRecordsImaging = () => {
    props.updateCaseSchedule({ imagingDocs: [] });
  }

  const handleOnDeleteRecordsImagingPage = (pageId) => {
    let updatedImagingDocs = clone(caseData.imagingDocs);
    updatedImagingDocs = updatedImagingDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ imagingDocs: updatedImagingDocs });
  };

  const handleOnChangeIsNoneImagingDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneImagingDocs: isNoneValue });
  };

  const handleOnRecordsImagingDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ imagingDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      imagingDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // --- Records consults docs event handlers
  const handleOnDeleteRecordsConsults = () => {
    props.updateCaseSchedule({ consultsDocs: [] });
  }

  const handleOnDeleteRecordsConsultsPage = (pageId) => {
    let updatedConsultsDocs = clone(caseData.consultsDocs);
    updatedConsultsDocs = updatedConsultsDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ consultsDocs: updatedConsultsDocs });
  };

  const handleOnChangeIsNoneConsultsDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneConsults: isNoneValue });
  };

  const handleOnRecordsConsultsDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ consultsDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      consultsDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // --- Consents surgery docs event handlers
  const handleOnDeleteConsentSurgery = () => {
    props.updateCaseSchedule({ consentSurgFormDocs: [] });
  }

  const handleOnDeleteConsentSurgeryPage = (pageId) => {
    let updatedConsentSurgeryDocs = clone(caseData.consentSurgFormDocs);
    updatedConsentSurgeryDocs = updatedConsentSurgeryDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ consentSurgFormDocs: updatedConsentSurgeryDocs });
  };

  const handleOnChangeIsNoneConsentSurgeryDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneConsentSurgFormDocs: isNoneValue });
  };

  const handleOnConsentSurgeryDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ consentSurgFormDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      consentSurgFormDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // --- Consents anesthesia docs event handlers
  const handleOnDeleteConsentAnesthesia = () => {
    props.updateCaseSchedule({ consentFormDocs: [] });
  }

  const handleOnDeleteConsentAnesthesiaPage = (pageId) => {
    let updatedConsentAnesthesiaDocs = clone(caseData.consentFormDocs);
    updatedConsentAnesthesiaDocs = updatedConsentAnesthesiaDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ consentFormDocs: updatedConsentAnesthesiaDocs });
  };

  const handleOnChangeIsNoneConsentAnesthesiaDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneConsentFormDocs: isNoneValue });
  };

  const handleOnConsentAnesthesiaDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ consentFormDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      consentFormDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // Nursing preOp docs event handlers
  const handleOnDeleteNursingPreOpDocs = () => {
    props.updateCaseSchedule({ nursingPreOpDocs: [] });
  };

  const handleOnDeleteNursingPreOpDocsPage = (pageId) => {
    let updatedNursingPreOpDocs = clone(caseData.nursingPreOpDocs);
    updatedNursingPreOpDocs = updatedNursingPreOpDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ nursingPreOpDocs: updatedNursingPreOpDocs });
  };

  const handleOnChangeIsNoneNursingPreOpDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneNursingPreOpDocs: isNoneValue });
  };

  const handleOnNursingPreOpDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ nursingPreOpDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      nursingPreOpDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // Nursing intraOP docs event handlers
  const handleOnDeleteNursingIntraOpDocs = () => {
    props.updateCaseSchedule({ nursingIntraOpDocs: [] });
  };

  const handleOnDeleteNursingIntraOpDocsPage = (pageId) => {
    let updatedNursingIntraOpDocs = clone(caseData.nursingIntraOpDocs);
    updatedNursingIntraOpDocs = updatedNursingIntraOpDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ nursingIntraOpDocs: updatedNursingIntraOpDocs });
  };

  const handleOnChangeIsNoneNursingIntraOpDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneNursingIntraOpDocs: isNoneValue });
  };

  const handleOnNursingIntraOpDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ nursingIntraOpDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      nursingIntraOpDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // Nursing postOp docs event handlers
  const handleOnDeleteNursingPostOpDocs = () => {
    props.updateCaseSchedule({ nursingPostOpDocs: [] });
  };

  const handleOnDeleteNursingPostOpDocsPage = (pageId) => {
    let updatedNursingPostOpDocs = clone(caseData.nursingPostOpDocs);
    updatedNursingPostOpDocs = updatedNursingPostOpDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ nursingPostOpDocs: updatedNursingPostOpDocs });
  };

  const handleOnChangeIsNoneNursingPostOpDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneNursingPostOpDocs: isNoneValue });
  };

  const handleOnNursingPostOpDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ nursingPostOpDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      nursingPostOpDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // Surgeon preOp docs event handlers
  const handleOnDeleteSurgeonPreOpDocs = () => {
    props.updateCaseSchedule({ surgeonPreOpDocs: [] });
  };

  const handleOnDeleteSurgeonPreOpDocsPage = (pageId) => {
    let updatedSurgeonPreOpDocs = clone(caseData.surgeonPreOpDocs);
    updatedSurgeonPreOpDocs = updatedSurgeonPreOpDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ surgeonPreOpDocs: updatedSurgeonPreOpDocs });
  };

  const handleOnChangeIsNoneSurgeonPreOpDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneSurgeonPreOpDocs: isNoneValue });
  };

  const handleOnSurgeonPreOpDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ surgeonPreOpDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      surgeonPreOpDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // Surgeon intraOP docs event handlers
  const handleOnDeleteSurgeonIntraOpDocs = () => {
    props.updateCaseSchedule({ surgeonIntraOpDocs: [] });
  };

  const handleOnDeleteSurgeonIntraOpDocsPage = (pageId) => {
    let updatedSurgeonIntraOpDocs = clone(caseData.surgeonIntraOpDocs);
    updatedSurgeonIntraOpDocs = updatedSurgeonIntraOpDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ surgeonIntraOpDocs: updatedSurgeonIntraOpDocs });
  };

  const handleOnChangeIsNoneSurgeonIntraOpDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneSurgeonIntraOpDocs: isNoneValue });
  };

  const handleOnSurgeonIntraOpDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ surgeonIntraOpDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      surgeonIntraOpDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // Surgeon postOp docs event handlers
  const handleOnDeleteSurgeonPostOpDocs = () => {
    props.updateCaseSchedule({ surgeonPostOpDocs: [] });
  };

  const handleOnDeleteSurgeonPostOpDocsPage = (pageId) => {
    let updatedSurgeonPostOpDocs = clone(caseData.surgeonPostOpDocs);
    updatedSurgeonPostOpDocs = updatedSurgeonPostOpDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ surgeonPostOpDocs: updatedSurgeonPostOpDocs });
  };

  const handleOnChangeIsNoneSurgeonPostOpDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneSurgeonPostOpDocs: isNoneValue });
  };

  const handleOnSurgeonPostOpDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ surgeonPostOpDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      surgeonPostOpDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // Anesthesia preop form docs
  const handleOnDeleteAnesthesiaPreOpDocs = () => {
    props.updateCaseSchedule({ preOpFormDocs: [] });
  }

  const handleOnDeleteAnesthesiaPreOpDocsPage = (pageId) => {
    let updatedAnesthesiaPreOpDocs = clone(caseData.preOpFormDocs);
    updatedAnesthesiaPreOpDocs = updatedAnesthesiaPreOpDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ preOpFormDocs: updatedAnesthesiaPreOpDocs });
  };

  const handleOnChangeIsNoneAnesthesiaPreOpDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNonePreOpFormDocs: isNoneValue });
  };

  const handleOnAnesthesiaPreOpDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ preOpFormDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      preOpFormDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // Anesthesia intraop form docs
  const handleOnDeleteAnesthesiaIntraOpDocs = () => {
    props.updateCaseSchedule({ intraOpFormDocs: [] });
  }

  const handleOnDeleteAnesthesiaIntraOpDocsPage = (pageId) => {
    let updatedAnesthesiaIntraOpDocs = clone(caseData.intraOpFormDocs);
    updatedAnesthesiaIntraOpDocs = updatedAnesthesiaIntraOpDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ intraOpFormDocs: updatedAnesthesiaIntraOpDocs });
  };

  const handleOnChangeIsNoneAnesthesiaIntraOpDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneIntraOpFormDocs: isNoneValue });
  };

  const handleOnAnesthesiaIntraOpDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ intraOpFormDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      intraOpFormDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // Anesthesia postop form docs
  const handleOnDeleteAnesthesiaPostOpDocs = () => {
    props.updateCaseSchedule({ postOpFormDocs: [] });
  }

  const handleOnDeleteAnesthesiaPostOpDocsPage = (pageId) => {
    let updatedAnesthesiaPostOpDocs = clone(caseData.postOpFormDocs);
    updatedAnesthesiaPostOpDocs = updatedAnesthesiaPostOpDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ postOpFormDocs: updatedAnesthesiaPostOpDocs });
  };

  const handleOnChangeIsNoneAnesthesiaPostOpDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNonePostOpFormDocs: isNoneValue });
  };

  const handleOnAnesthesiaPostOpDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ postOpFormDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      postOpFormDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // --- Orders preop docs event handlers
  const handleOnDeleteOrdersPreOpDocs = () => {
    props.updateCaseSchedule({ ordersPreOpDocs: [] });
  }

  const handleOnDeleteOrdersPreOpDocsPage = (pageId) => {
    let updatedOrdersPreOpDocs = clone(caseData.ordersPreOpDocs);
    updatedOrdersPreOpDocs = updatedOrdersPreOpDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ ordersPreOpDocs: updatedOrdersPreOpDocs });
  };

  const handleOnChangeIsNoneOrdersPreOpDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneOrdersPreOpDocs: isNoneValue });
  };

  const handleOnOrdersPreOpDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ ordersPreOpDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      ordersPreOpDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // --- Orders intraop docs event handlers
  const handleOnDeleteOrdersIntraOpDocs = () => {
    props.updateCaseSchedule({ ordersIntraOpDocs: [] });
  }

  const handleOnDeleteOrdersIntraOpDocsPage = (pageId) => {
    let updatedOrdersIntraOpDocs = clone(caseData.ordersIntraOpDocs);
    updatedOrdersIntraOpDocs = updatedOrdersIntraOpDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ ordersIntraOpDocs: updatedOrdersIntraOpDocs });
  };

  const handleOnChangeIsNoneOrdersIntraOpDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneOrdersIntraOpDocs: isNoneValue });
  };

  const handleOnOrdersIntraOpDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ ordersIntraOpDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      ordersIntraOpDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // --- Orders postOp docs event handlers
  const handleOnDeleteOrdersPostOpDocs = () => {
    props.updateCaseSchedule({ ordresPostOpDocs: [] });
  }

  const handleOnDeleteOrdersPostOpDocsPage = (pageId) => {
    let updatedOrdersPostOpDocs = clone(caseData.ordresPostOpDocs);
    updatedOrdersPostOpDocs = updatedOrdersPostOpDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ ordresPostOpDocs: updatedOrdersPostOpDocs });
  };

  const handleOnChangeIsNoneOrdersPostOpDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneOrdersPostOpDocs: isNoneValue });
  };

  const handleOnOrdersPostOpDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ ordresPostOpDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      ordresPostOpDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  // --- Discharge docs event handlers
  const handleOnDeleteDischargeDocs = () => {
    props.updateCaseSchedule({ dischargeDocs: [] });
  }

  const handleOnDeleteDischargeDocsPage = (pageId) => {
    let updatedDischargeDocs = clone(caseData.dischargeDocs);
    updatedDischargeDocs = updatedDischargeDocs.filter(doc => doc.id !== pageId);
    props.updateCaseSchedule({ dischargeDocs: updatedDischargeDocs });
  };

  const handleOnChangeIsNoneDischargeDocs = (isNoneValue) => {
    props.updateCaseSchedule({ isNoneDischargeDocs: isNoneValue });
  };

  const handleOnDischargeDocsUploaded = (updatedDocs) => {
    props.updateCaseSchedule({ dischargeDocs: updatedDocs });
    props.saveCaseSchedule({
      ...props.activeCaseSchedule,
      dischargeDocs: updatedDocs,
      caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
      patientId: apd._id
    });
  };

  const handleOnCloseDocWriter = (updatedPages, formType) => {
    switch (formType) {
      case FORM_TYPE.faceSheet:
        props.updateCaseSchedule({ faceSheetDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          faceSheetDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsLabs:
        props.updateCaseSchedule({ labsDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          labsDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsEkg:
        props.updateCaseSchedule({ ekgDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ekgDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsImaging:
        props.updateCaseSchedule({ imagingDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          imagingDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsConsults:
        props.updateCaseSchedule({ consultsDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          consultsDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.consentsSurgery:
        props.updateCaseSchedule({ consentSurgFormDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          consentSurgFormDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.consentsAnesthesia:
        props.updateCaseSchedule({ consentFormDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          consentFormDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.nursingPreOp:
        props.updateCaseSchedule({ nursingPreOpDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          nursingPreOpDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.nursingIntraOp:
        props.updateCaseSchedule({ nursingIntraOpDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          nursingIntraOpDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.nursingPostOp:
        props.updateCaseSchedule({ nursingPostOpDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          nursingPostOpDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.surgeonPreOp:
        props.updateCaseSchedule({ surgeonPreOpDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          surgeonPreOpDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.surgeonIntraOp:
        props.updateCaseSchedule({ surgeonIntraOpDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          surgeonIntraOpDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.surgeonPostOp:
        props.updateCaseSchedule({ surgeonPostOpDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          surgeonPostOpDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.anesthesiaPreOp:
        props.updateCaseSchedule({ preOpFormDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          preOpFormDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.anesthesiaIntraOp:
        props.updateCaseSchedule({ intraOpFormDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          intraOpFormDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.anesthesiaPostOp:
        props.updateCaseSchedule({ postOpFormDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          postOpFormDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.ordersPreOp:
        props.updateCaseSchedule({ ordersPreOpDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ordersPreOpDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.ordersIntraOp:
        props.updateCaseSchedule({ ordersIntraOpDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ordersIntraOpDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.ordersPostOp:
        props.updateCaseSchedule({ ordresPostOpDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ordresPostOpDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.discharge:
        props.updateCaseSchedule({ dischargeDocs: updatedPages });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          dischargeDocs: updatedPages,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      default:
        console.log('Invalid form type!');
    }

    setShowDocWriter(false);
    setDocWriterData({});
  };

  const handleOnCancelDocWriter = () => {
    setShowDocWriter(false);
    setDocWriterData({});
  };

  const handleOnDocWriterPageSelected = (pageId) => { };

  const handleOnCloseCameraDrawer = () => {
    setScanDocumentData({});
    setShowCameraDrawer(false);
    setShowCamInspectorDrawer(false);
  };

  const handleOnScanCameraDrawer = (imageDataUrl) => {
    const imageExtension = findFileExtensionByMimeType(findBase64ImageMimeType(imageDataUrl));
    let updatedCaseDocs = clone(scanDocumentData.caseDocs);
    const newDocId = Math.random().toString(36).slice(2);
    const newFileName = `file_${Math.random().toString(36).slice(2)}`;

    updatedCaseDocs = updatedCaseDocs.concat({
      id: newDocId,
      imageUrl: '',
      fileName: `${newFileName}${imageExtension}`,
      compositeImage: imageDataUrl
    });

    // Update scan document data object
    setScanDocumentData((prvState) => {
      return {
        ...prvState,
        caseDocs: updatedCaseDocs,
        selectedPageId: newDocId,
        newImageUrl: imageDataUrl
      };
    });

    // Hide camera drawer and show CamInspectorDrawer
    setShowCameraDrawer(false);
    setShowCamInspectorDrawer(true);
  };

  const handleOnCancelScanDocs = () => {
    setShowCameraDrawer(false);
    setShowCamInspectorDrawer(false);
    setScanDocumentData({});
  };

  const handleOnRetakeScanDocs = () => {
    switch (scanDocumentData.formType) {
      case FORM_TYPE.faceSheet:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.faceSheetDocs)
          };
        });
        break;
      case FORM_TYPE.recordsLabs:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.labsDocs)
          };
        });
        break;
      case FORM_TYPE.recordsEkg:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.ekgDocs)
          };
        });
        break;
      case FORM_TYPE.recordsImaging:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.imagingDocs)
          };
        });
        break;
      case FORM_TYPE.recordsConsults:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.consultsDocs)
          };
        });
        break;
      case FORM_TYPE.consentsSurgery:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.consentSurgFormDocs)
          };
        });
        break;
      case FORM_TYPE.consentsAnesthesia:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.consentFormDocs)
          };
        });
        break;
      case FORM_TYPE.nursingPreOp:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.nursingPreOpDocs)
          };
        });
        break;
      case FORM_TYPE.nursingIntraOp:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.nursingIntraOpDocs)
          };
        });
        break;
      case FORM_TYPE.nursingPostOp:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.nursingPostOpDocs)
          };
        });
        break;
      case FORM_TYPE.surgeonPreOp:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.surgeonPreOpDocs)
          };
        });
        break;
      case FORM_TYPE.surgeonIntraOp:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.surgeonIntraOpDocs)
          };
        });
        break;
      case FORM_TYPE.surgeonPostOp:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.surgeonPostOpDocs)
          };
        });
        break;
      case FORM_TYPE.anesthesiaPreOp:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.preOpFormDocs)
          };
        });
        break;
      case FORM_TYPE.anesthesiaIntraOp:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.intraOpFormDocs)
          };
        });
        break;
      case FORM_TYPE.anesthesiaPostOp:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.ordersIntraOpDocs)
          };
        });
        break;
      case FORM_TYPE.ordersPreOp:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.ordersPreOpDocs)
          };
        });
        break;
      case FORM_TYPE.ordersIntraOp:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.ordersIntraOpDocs)
          };
        });
        break;
      case FORM_TYPE.ordersPostOp:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.ordresPostOpDocs)
          };
        });
        break;
      case FORM_TYPE.discharge:
        setScanDocumentData((prvState) => {
          return {
            ...prvState,
            caseDocs: clone(acs.dischargeDocs)
          };
        });
        break;
    }

    setShowCameraDrawer(true);
    setShowCamInspectorDrawer(false);
  };

  const handleOnCloseScanDocs = () => {
    const updatedDocs = clone(scanDocumentData.caseDocs);
    switch (scanDocumentData.formType) {
      case FORM_TYPE.faceSheet:
        props.updateCaseSchedule({ faceSheetDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          faceSheetDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsLabs:
        props.updateCaseSchedule({ labsDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          labsDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsEkg:
        props.updateCaseSchedule({ ekgDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ekgDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsImaging:
        props.updateCaseSchedule({ imagingDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          imagingDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.recordsConsults:
        props.updateCaseSchedule({ consultsDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          consultsDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.consentsSurgery:
        props.updateCaseSchedule({ consentSurgFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          consentSurgFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.consentsAnesthesia:
        props.updateCaseSchedule({ consentFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          consentFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.nursingPreOp:
        props.updateCaseSchedule({ nursingPreOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          nursingPreOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.nursingIntraOp:
        props.updateCaseSchedule({ nursingIntraOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          nursingIntraOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.nursingPostOp:
        props.updateCaseSchedule({ nursingPostOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          nursingPostOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.surgeonPreOp:
        props.updateCaseSchedule({ surgeonPreOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          surgeonPreOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.surgeonIntraOp:
        props.updateCaseSchedule({ surgeonIntraOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          surgeonIntraOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.surgeonPostOp:
        props.updateCaseSchedule({ surgeonPostOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          surgeonPostOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.anesthesiaPreOp:
        props.updateCaseSchedule({ preOpFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          preOpFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.anesthesiaIntraOp:
        props.updateCaseSchedule({ intraOpFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          intraOpFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.anesthesiaPostOp:
        props.updateCaseSchedule({ postOpFormDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          postOpFormDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.ordersPreOp:
        props.updateCaseSchedule({ ordersPreOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ordersPreOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.ordersIntraOp:
        props.updateCaseSchedule({ ordersIntraOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ordersIntraOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.ordersPostOp:
        props.updateCaseSchedule({ ordresPostOpDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          ordresPostOpDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
      case FORM_TYPE.discharge:
        props.updateCaseSchedule({ dischargeDocs: updatedDocs });
        props.saveCaseSchedule({
          ...props.activeCaseSchedule,
          dischargeDocs: updatedDocs,
          caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
          patientId: apd._id
        });
        break;
    }

    setShowCameraDrawer(false);
    setShowCamInspectorDrawer(false);
    setScanDocumentData({});
  };

  const handleOnCloseCaseDataDrawer = () => {
    setShowCaseDataDrawer(false);
  };

  const handleOnActivateCaseDataDrawerTab = (tabName) => {
    setCaseDataDrawerActiveTab(tabName);
  };

  const handleOnDeletePatient = () => {
    props.deletePatientFromCaseSchedule();
  };

  const handleOnAddPatient = (pData) => {
    props.addPatientToExistingCaseSchedule(pData);
  };

  const handleOnReloadPatientData = () => {
    props.reloadExistingCaseSchedulePatientData(apd._id);
  };

  // --- Active document tab
  let caseDataDrawerActiveComponent = null;
  switch (caseDataDrawerActiveTab) {
    case 'case':
      caseDataDrawerActiveComponent = (
        <CaseTab
          isDashboard
          caseData={caseData}
          onClose={handleOnCloseCaseDataDrawer}
        />
      );
      break;
    case 'patient':
      caseDataDrawerActiveComponent = (
        <PatientTab
          isDashboard
          onDeletePatient={handleOnDeletePatient}
          onAddPatient={handleOnAddPatient}
          onReloadPatientData={handleOnReloadPatientData}
        />
      );
      break;
    case 'payers':
      caseDataDrawerActiveComponent = (
        <PayerTab
          patientData={apd}
          onReloadPatientData={handleOnReloadPatientData}
        />
      );
      break;
    default:
      caseDataDrawerActiveComponent = null;
  }

  // --- Anesthesia procedures search menu items
  const procedureSearchItems = [];
  // Append search text
  if (anesthesiaProcedureValue && anesthesiaProcedureValue.length > 0) {
    procedureSearchItems.push(
      <div
        key='88888999SurgProcedure'
        className={`fx fx__v-center ${css(styles.menuItem)}`}
        onClick={(e) => {
          e.stopPropagation();
          const tmpProc = {
            id: Math.random().toString(36).slice(2),
            text: anesthesiaProcedureValue,
            code: '',
            description: anesthesiaProcedureValue,
            npi: 0
          };
          setShowAnesthesiaProceduresSearchMenu(false);
          setAnesthesiaProcedureValue('');
          let updatedAnesthesiaProcedures = clone(caseData.anesthesiaProcedures);
          updatedAnesthesiaProcedures = updatedAnesthesiaProcedures.concat(tmpProc);
          props.updateCaseSchedule({ anesthesiaProcedures: updatedAnesthesiaProcedures });
        }}
      >
        <div className='ml-2'>
          <div style={{ height: '40px', display: 'flex', alignItems: 'center' }}>
            {anesthesiaProcedureValue}
          </div>
        </div>
      </div>
    );
  }

  if (anesthesiaProceduresSearchResults.length > 0) {
    // Append search results
    anesthesiaProceduresSearchResults.forEach((el) => {
      procedureSearchItems.push(
        <div
          key={el._id}
          className={`fx fx__v-center ${css(styles.menuItem)}`}
          onClick={(e) => {
            e.stopPropagation();
            setShowAnesthesiaProceduresSearchMenu(false);
            setAnesthesiaProcedureValue('');
            let updatedAnesthesiaProcedures = clone(caseData.anesthesiaProcedures);
            if (updatedAnesthesiaProcedures.findIndex(item => item.id === el._id) < 0) {
              updatedAnesthesiaProcedures = updatedAnesthesiaProcedures.concat({
                id: el._id,
                text: `${el.description}`,
                code: el.code,
                description: `${el.description}`,
                npi: el.npi
              });
              props.updateCaseSchedule({ anesthesiaProcedures: updatedAnesthesiaProcedures });
            }
          }}
        >
          <div className='ml-2'>
            <div style={{ height: '40px', display: 'flex', alignItems: 'center' }}>
              {getHighlightedText(el.description, anesthesiaProcedureValue)}
            </div>
          </div>
        </div>
      );
    });
  }

  // ----- Active doc component
  let activeDocComponent = null;
  switch (activeTab) {
    case TABS[0].text: // FaceSheet tab
      activeDocComponent = (
        <CaseDoc
          key='facesheet_docs'
          showFlag
          formType={FORM_TYPE.faceSheet}
          flagType={FLAG_VALUE.critical}
          scheduleId={caseData._id}
          caseDocs={caseData.faceSheetDocs}
          isNone={caseData.isNoneFaceSheet}
          onDeleteDocument={handleOnDeleteFaceSheet}
          onDeletePage={handleOnDeleteFacesheetPage}
          onChangeIsNone={handleOnChangeIsNoneFaceSheet}
          onCaseDocUploaded={handleOnFacesheetDocsUploaded}
          onEdit={handleOnEditDocs}
          onScanDocument={handleOnScanDocs}
          onShowCropper={handleOnShowCropper}
          onShowCamera={handleOnShowCamera}
          onShowDocWriter={handleOnShowDocWriter}
        />
      );
      break;
    case TABS[1].text: // Records tab
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="records_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* Labs */}
            <TabItem
              key='1199'
              tabKey='1199'
              parentKey='11999'
              text='Labs'
              isActive={recordsSubTab === 'labs'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (caseData.isNoneLabDocs) {
                  return IS_NONE_ICON;
                }
                if (Array.isArray(caseData.labsDocs) && caseData.labsDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                // width: window.innerWidth > 720 ? '110px' : '25%',
                // minWidth: window.innerWidth > 720 ? '110px' : '25%',
                // maxWidth: window.innerWidth > 720 ? '110px' : '25%',
                // background: recordsSubTab === 'labs' ? '#184878' : '#083050'
                width: '80px',
                minWidth: '80px',
                maxWidth: '80px',
                background: '#0050A0',
                borderBottom: recordsSubTab === 'labs' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setRecordsSubTab('labs');
              }}
            >
              Labs
            </TabItem>
            {/* EKG */}
            <TabItem
              key='12992001'
              tabKey='12992001'
              parentKey='12993000'
              text='EKG'
              isActive={recordsSubTab === 'ekg'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (caseData.isNoneEkgDocs) {
                  return IS_NONE_ICON;
                }
                if (Array.isArray(caseData.ekgDocs) && caseData.ekgDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                // width: window.innerWidth > 720 ? '110px' : '25%',
                // minWidth: window.innerWidth > 720 ? '110px' : '25%',
                // maxWidth: window.innerWidth > 720 ? '110px' : '25%',
                // background: recordsSubTab === 'ekg' ? '#184878' : '#083050',
                width: '80px',
                minWidth: '80px',
                maxWidth: '80px',
                background: '#0050A0',
                borderLeft: '1px solid black',
                borderBottom: recordsSubTab === 'ekg' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setRecordsSubTab('ekg');
              }}
            >
              EKG
            </TabItem>
            {/* Imaging */}
            <TabItem
              key='19192002'
              tabKey='19192002'
              parentKey='19194000'
              text='PostOp'
              isActive={recordsSubTab === 'imaging'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (caseData.isNoneImagingDocs) {
                  return IS_NONE_ICON;
                }
                if (Array.isArray(caseData.imagingDocs) && caseData.imagingDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                // width: window.innerWidth > 720 ? '110px' : '25%',
                // minWidth: window.innerWidth > 720 ? '110px' : '25%',
                // maxWidth: window.innerWidth > 720 ? '110px' : '25%',
                // background: recordsSubTab === 'imaging' ? '#184878' : '#083050',
                width: '85px',
                minWidth: '85px',
                maxWidth: '85px',
                background: '#0050A0',
                borderLeft: '1px solid black',
                borderBottom: recordsSubTab === 'imaging' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setRecordsSubTab('imaging');
              }}
            >
              Imaging
            </TabItem>
            {/* Consults */}
            <TabItem
              key='2003'
              tabKey='2003'
              parentKey='5000'
              text='Consults'
              isActive={recordsSubTab === 'consults'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (caseData.isNoneConsults) {
                  return IS_NONE_ICON;
                }
                if (Array.isArray(caseData.consultsDocs) && caseData.consultsDocs.length > 0) {
                  return SUCCESS_ICON;
                } else {
                  return CRITIAL_ICON;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                // width: window.innerWidth > 720 ? '110px' : '25%',
                // minWidth: window.innerWidth > 720 ? '110px' : '25%',
                // maxWidth: window.innerWidth > 720 ? '110px' : '25%',
                // background: recordsSubTab === 'consults' ? '#184878' : '#083050',
                width: '90px',
                minWidth: '90px',
                maxWidth: '90px',
                background: '#0050A0',
                borderLeft: '1px solid black',
                borderBottom: recordsSubTab === 'consults' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setRecordsSubTab('consults');
              }}
            >
              Consults
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {recordsSubTab === 'labs' &&
              <CaseDoc
                key='records_labs_docs'
                showFlag
                formType={FORM_TYPE.recordsLabs}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.labsDocs}
                isNone={caseData.isNoneLabDocs}
                onDeleteDocument={handleOnDeleteRecordsLabs}
                onDeletePage={handleOnDeleteRecordsLabsPage}
                onChangeIsNone={handleOnChangeIsNoneLabsDocs}
                onCaseDocUploaded={handleOnRecordsLabsDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
            {recordsSubTab === 'ekg' &&
              <CaseDoc
                key='records_ekg_docs'
                showFlag
                formType={FORM_TYPE.recordsEkg}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.ekgDocs}
                isNone={caseData.isNoneEkgDocs}
                onDeleteDocument={handleOnDeleteRecordsEkg}
                onDeletePage={handleOnDeleteRecordsEkgPage}
                onChangeIsNone={handleOnChangeIsNoneEkgDocs}
                onCaseDocUploaded={handleOnRecordsEkgDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
            {recordsSubTab === 'imaging' &&
              <CaseDoc
                key='records_imaging_docs'
                showFlag
                formType={FORM_TYPE.recordsImaging}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.imagingDocs}
                isNone={caseData.isNoneImagingDocs}
                onDeleteDocument={handleOnDeleteRecordsImaging}
                onDeletePage={handleOnDeleteRecordsImagingPage}
                onChangeIsNone={handleOnChangeIsNoneImagingDocs}
                onCaseDocUploaded={handleOnRecordsImagingDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
            {recordsSubTab === 'consults' &&
              <CaseDoc
                key='records_consults_docs'
                showFlag
                formType={FORM_TYPE.recordsConsults}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.consultsDocs}
                isNone={caseData.isNoneConsults}
                onDeleteDocument={handleOnDeleteRecordsConsults}
                onDeletePage={handleOnDeleteRecordsConsultsPage}
                onChangeIsNone={handleOnChangeIsNoneConsultsDocs}
                onCaseDocUploaded={handleOnRecordsConsultsDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
          </div>
        </>
      );
      break;
    case TABS[2].text: // Consents tab
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="consent_forms_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* Consents - Surgery sub tab */}
            <TabItem
              key='2334'
              tabKey='2334'
              parentKey='1223'
              text='Surgery'
              isActive={consentActiveTab === 'surgery'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (caseData.isNoneConsentSurgFormDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (Array.isArray(caseData.consentSurgFormDocs) && caseData.consentSurgFormDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '90px',
                minWidth: '90px',
                maxWidth: '90px',
                // background: consentActiveTab === 'surgery' ? '#184878' : '#083050',
                background: '#0050A0',
                borderBottom: consentActiveTab === 'surgery' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setConsentActiveTab('surgery');
              }}
            >
              Surgery
            </TabItem>
            {/* Consents - Anesthesia sub tab */}
            <TabItem
              key='2333'
              tabKey='2333'
              parentKey='1222'
              text='Anesthesia'
              isActive={consentActiveTab === 'anesthesia'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (caseData.isNoneConsentFormDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (Array.isArray(caseData.consentFormDocs) && caseData.consentFormDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                // background: consentActiveTab === 'anesthesia' ? '#184878' : '#083050',
                background: '#0050A0',
                borderLeft: '1px solid black',
                borderBottom: consentActiveTab === 'anesthesia' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setConsentActiveTab('anesthesia');
              }}
            >
              Anesthesia
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {consentActiveTab === 'surgery' &&
              <CaseDoc
                key='consent_surgery_docs'
                showFlag
                formType={FORM_TYPE.consentsSurgery}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.consentSurgFormDocs}
                isNone={caseData.isNoneConsentSurgFormDocs}
                onDeleteDocument={handleOnDeleteConsentSurgery}
                onDeletePage={handleOnDeleteConsentSurgeryPage}
                onChangeIsNone={handleOnChangeIsNoneConsentSurgeryDocs}
                onCaseDocUploaded={handleOnConsentSurgeryDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
            {consentActiveTab === 'anesthesia' &&
              <CaseDoc
                key='consent_anesthesia_docs'
                showFlag
                formType={FORM_TYPE.consentsAnesthesia}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.consentFormDocs}
                isNone={caseData.isNoneConsentFormDocs}
                onDeleteDocument={handleOnDeleteConsentAnesthesia}
                onDeletePage={handleOnDeleteConsentAnesthesiaPage}
                onChangeIsNone={handleOnChangeIsNoneConsentAnesthesiaDocs}
                onCaseDocUploaded={handleOnConsentAnesthesiaDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
          </div>
        </>
      );
      break;
    case TABS[3].text: // Anesthesia tab
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="anesthesia_forms_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* PreOp Tab */}
            <TabItem
              key='2000'
              tabKey='2000'
              parentKey='2000'
              text='PreOp'
              isActive={anesthesiaSubTab === 'preOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                const {
                  asaScore = '',
                  isNonePreOpFormDocs = false,
                  preOpFormDocs = []
                } = caseData;
                const anesthesiaDataFlagValue = asaScore.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
                if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
                  return <DocumentCriticalIcon height={20} />;
                } else {
                  if (isNonePreOpFormDocs) {
                    return <DocumentSuccessIcon height={20} />;
                  }
                  if (Array.isArray(preOpFormDocs) && preOpFormDocs.length > 0) {
                    return <DocumentSuccessIcon height={20} />;
                  } else {
                    return <DocumentCriticalIcon height={20} />;
                  }
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '90px',
                minWidth: '90px',
                maxWidth: '90px',
                background: '#0050A0',
                borderBottom: anesthesiaSubTab === 'preOp' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setAnesthesiaSubTab('preOp');
              }}
            >
              PreOp
            </TabItem>
            {/* IntraOp Tab */}
            <TabItem
              key='2001'
              tabKey='2001'
              parentKey='3000'
              text='IntraOp'
              isActive={anesthesiaSubTab === 'intraOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                const {
                  anesthesiaTypes = [],
                  anesthesiaModifiers,
                  isNoneIntraOpFormDocs = false,
                  intraOpFormDocs = []
                } = caseData;
                let anesthesiaDataFlagValue = FLAG_VALUE.critical;
                if (anesthesiaTypes.length > 0 && anesthesiaModifiers.length > 0) {
                  anesthesiaDataFlagValue = FLAG_VALUE.success;
                }
                if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
                  return <DocumentCriticalIcon height={20} />;
                } else {
                  if (isNoneIntraOpFormDocs) {
                    return <DocumentSuccessIcon height={20} />;
                  }
                  if (Array.isArray(intraOpFormDocs) && intraOpFormDocs.length > 0) {
                    return <DocumentSuccessIcon height={20} />;
                  } else {
                    return <DocumentCriticalIcon height={20} />;
                  }
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '90px',
                minWidth: '90px',
                maxWidth: '90px',
                background: '#0050A0',
                borderLeft: '1px solid black',
                borderBottom: anesthesiaSubTab === 'intraOp' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setAnesthesiaSubTab('intraOp');
              }}
            >
              IntraOp
            </TabItem>
            {/* PostOp */}
            <TabItem
              key='2002'
              tabKey='2002'
              parentKey='4000'
              text='PostOp'
              isActive={anesthesiaSubTab === 'postOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (caseData.isNonePostOpFormDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (Array.isArray(caseData.postOpFormDocs) && caseData.postOpFormDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '90px',
                minWidth: '90px',
                maxWidth: '90px',
                background: '#0050A0',
                borderLeft: '1px solid black',
                borderBottom: anesthesiaSubTab === 'postOp' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setAnesthesiaSubTab('postOp');
              }}
            >
              PostOp
            </TabItem>
            {/* Procedures */}
            <TabItem
              key='2003'
              tabKey='2003'
              parentKey='5000'
              text='Procedures'
              isActive={anesthesiaSubTab === 'procedures'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                const {
                  isNoneAnesthesiaProcedure = false,
                  anesthesiaProcedures = []
                } = caseData;
                if (isNoneAnesthesiaProcedure) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (Array.isArray(anesthesiaProcedures) && anesthesiaProcedures.length === 0) {
                  return <DocumentCriticalIcon height={20} />;
                }
                if (Array.isArray(anesthesiaProcedures) && anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
                  return <DocumentWarningIcon height={20} />;
                } else {
                  return <DocumentSuccessIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                background: '#0050A0',
                borderLeft: '1px solid black',
                borderBottom: anesthesiaSubTab === 'procedures' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setAnesthesiaSubTab('procedures');
              }}
            >
              Procedures
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {anesthesiaSubTab === 'preOp' &&
              <PreOp
                showFlag
                formType={FORM_TYPE.anesthesiaPreOp}
                asaScore={caseData.asaScore}
                scheduleId={caseData._id}
                onChangeAsaScore={(asa) => {
                  props.updateCaseSchedule({ asaScore: asa });
                }}
                flagType={FLAG_VALUE.critical}
                caseDocs={caseData.preOpFormDocs}
                isNone={caseData.isNonePreOpFormDocs}
                onDeleteDocument={handleOnDeleteAnesthesiaPreOpDocs}
                onDeletePage={handleOnDeleteAnesthesiaPreOpDocsPage}
                onChangeIsNone={handleOnChangeIsNoneAnesthesiaPreOpDocs}
                onCaseDocUploaded={handleOnAnesthesiaPreOpDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
            {anesthesiaSubTab === 'intraOp' &&
              <IntraOp
                showFlag
                formType={FORM_TYPE.anesthesiaIntraOp}
                scheduleId={caseData._id}
                anesthesiaTypes={caseData.anesthesiaTypes}
                onChangeAnesthesiaTypes={(data) => {
                  props.updateCaseSchedule({ anesthesiaTypes: data });
                }}
                anesthesiaModifiers={caseData.anesthesiaModifiers}
                onChangeAnesthesiaModifiers={(data) => {
                  props.updateCaseSchedule({ anesthesiaModifiers: data });
                }}
                flagType={FLAG_VALUE.critical}
                caseDocs={caseData.intraOpFormDocs}
                isNone={caseData.isNoneIntraOpFormDocs}
                onDeleteDocument={handleOnDeleteAnesthesiaIntraOpDocs}
                onDeletePage={handleOnDeleteAnesthesiaIntraOpDocsPage}
                onChangeIsNone={handleOnChangeIsNoneAnesthesiaIntraOpDocs}
                onCaseDocUploaded={handleOnAnesthesiaIntraOpDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
            {anesthesiaSubTab === 'postOp' &&
              <CaseDoc
                key='anesthesia_postop_docs'
                showFlag
                formType={FORM_TYPE.anesthesiaPostOp}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.postOpFormDocs}
                isNone={caseData.isNonePostOpFormDocs}
                onDeleteDocument={handleOnDeleteAnesthesiaPostOpDocs}
                onDeletePage={handleOnDeleteAnesthesiaPostOpDocsPage}
                onChangeIsNone={handleOnChangeIsNoneAnesthesiaPostOpDocs}
                onCaseDocUploaded={handleOnAnesthesiaPostOpDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
            {anesthesiaSubTab === 'procedures' &&
              <div className={`m-2 ${css(styles.anesthesisProcedureTabContainer)}`}>
                <div>
                  <CaseField
                    className='ml-0 mb-1'
                    heading='Anesthesia Procedures'
                    showNoneFlag={caseData.isNoneAnesthesiaProcedure}
                    showAlert={(() => {
                      const { isNoneAnesthesiaProcedure = false, anesthesiaProcedures = [] } = caseData;

                      if (!isNoneAnesthesiaProcedure) {
                        if (Array.isArray(anesthesiaProcedures) && anesthesiaProcedures.length === 0) {
                          return true;
                        }
                        return false;
                      }
                    })()}
                    showIncomplete={(() => {
                      const { isNoneAnesthesiaProcedure = false, anesthesiaProcedures = [] } = caseData;
                      if (Array.isArray(anesthesiaProcedures) && anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
                        return true;
                      } else {
                        return false;
                      }
                    })()}
                    showPlusIcon={false}
                  />
                  <Checkbox
                    text='None'
                    isChecked={caseData.isNoneAnesthesiaProcedure}
                    onChange={(val) => {
                      console.log(val);
                      props.updateCaseSchedule({
                        anesthesiaProcedures: [],
                        otherAnesthesiaProcedures: [],
                        isNoneAnesthesiaProcedure: !caseData.isNoneAnesthesiaProcedure
                      });
                    }}
                  />
                </div>
                {/* Anesthesia procedure value textbox & search menu */}
                <div
                  ref={procedureSearchMenuRef}
                  className='mt-2'
                  style={{
                    borderRadius: '4px',
                    position: 'relative',
                    display: caseData.isNoneAnesthesiaProcedure ? 'none' : ''
                  }}
                >
                  <TextInput
                    value={anesthesiaProcedureValue}
                    isDisabled={caseData.isNoneAnesthesiaProcedure}
                    placeholder="Search Anesthesia Procedure(s)"
                    disabled={caseData.isNoneAnesthesiaProcedure}
                    style={{
                      width: `${DRAWER_WIDTH}px`,
                      fontSize: '14px',
                      padding: '0 4px',
                      color: blue[200],
                      borderRadius: '4px'
                    }}
                    onChange={(e) => {
                      const { value } = e.target;
                      setAnesthesiaProcedureValue(value);
                    }}
                  />
                  {showAnesthesiaProceduresSearchMenu &&
                    <PopupMenu
                      top={35}
                      left={0}
                      zIndex='1550'
                      position="absolute"
                      visible={showAnesthesiaProceduresSearchMenu}
                      parent={procedureSearchMenuRef}
                      onOutsideClick={() => { setShowAnesthesiaProceduresSearchMenu(false); }}
                    >
                      <div className={css(styles.searchPopupMenuContainer)}>
                        {procedureSearchItems.length > 0 &&
                          <div className={`pl-2 fx fx__v-center ${css(styles.suggResults)}`}>
                            Suggested Results
                          </div>
                        }
                        {procedureSearchItems}
                      </div>
                    </PopupMenu>
                  }
                </div>
                {/* Anesthesia procedures cards */}
                {caseData.anesthesiaProcedures && Array.isArray(caseData.anesthesiaProcedures) && caseData.anesthesiaProcedures.map(dx => {
                  return (
                    <CaseUserCard
                      className='mt-1'
                      avatarImageUrl={null}
                      showCloseIcon
                      noAvatar
                      key={dx.id || dx._id}
                      dataId={dx.id || dx._id}
                      onClose={(id) => {
                        let updatedAnesthesiaProcedures = clone(caseData.anesthesiaProcedures);
                        updatedAnesthesiaProcedures = updatedAnesthesiaProcedures.filter(el => {
                          console.log(id);
                          console.log(el);
                          if (el.id) {
                            console.log(`Value of el.id !== id = ${el.id !== id}`);
                            return el.id !== id;
                          }
                          return el._id !== id;
                        })
                        props.updateCaseSchedule({ anesthesiaProcedures: updatedAnesthesiaProcedures });
                      }}
                    >
                      <div className='ml-2'>
                        {dx.npi === 0 &&
                          <i
                            className='font-16 mr-2 fa fa-bell'
                            style={{ color: salmon[500] }}
                          />
                        }
                        <span>
                          {dx.description}
                        </span>
                      </div>
                    </CaseUserCard>
                  );
                })}
              </div>
            }
          </div>
        </>
      );
      break;
    case TABS[4].text: // Surgeon tab
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="surgeon_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* PreOp sub tab */}
            <TabItem
              key='663112'
              tabKey='6631121'
              parentKey='6631122'
              text='PreOp'
              isActive={surgeonSubTab === 'preOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (caseData.isNoneSurgeonPreOpDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (Array.isArray(caseData.surgeonPreOpDocs) && caseData.surgeonPreOpDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '90px',
                minWidth: '90px',
                maxWidth: '90px',
                background: '#0050A0',
                borderBottom: surgeonSubTab === 'preOp' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setSurgeonSubTab('preOp');
              }}
            >
              PreOp
            </TabItem>
            {/* IntraOp sub tab */}
            <TabItem
              key='663113'
              tabKey='6631131'
              parentKey='6631132'
              text='IntraOp'
              isActive={surgeonSubTab === 'intraOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (caseData.isNoneSurgeonIntraOpDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (Array.isArray(caseData.surgeonIntraOpDocs) && caseData.surgeonIntraOpDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '90px',
                minWidth: '90px',
                maxWidth: '90px',
                background: '#0050A0',
                borderLeft: '1px solid black',
                borderBottom: surgeonSubTab === 'intraOp' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setSurgeonSubTab('intraOp');
              }}
            >
              IntraOp
            </TabItem>
            {/* PostOp sub tab */}
            <TabItem
              key='663114'
              tabKey='6631143'
              parentKey='6631434'
              text='Anesthesia'
              isActive={surgeonSubTab === 'postOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (caseData.isNoneSurgeonPostOpDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (Array.isArray(caseData.surgeonPostOpDocs) && caseData.surgeonPostOpDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '90px',
                minWidth: '90px',
                maxWidth: '90px',
                background: '#0050A0',
                borderLeft: '1px solid black',
                borderBottom: surgeonSubTab === 'postOp' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setSurgeonSubTab('postOp');
              }}
            >
              PostOp
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {surgeonSubTab === 'preOp' &&
              <CaseDoc
                key='surgeon_pre_op_docs'
                showFlag
                formType={FORM_TYPE.surgeonPreOp}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.surgeonPreOpDocs}
                isNone={caseData.isNoneSurgeonPreOpDocs}
                onDeleteDocument={handleOnDeleteSurgeonPreOpDocs}
                onDeletePage={handleOnDeleteSurgeonPreOpDocsPage}
                onChangeIsNone={handleOnChangeIsNoneSurgeonPreOpDocs}
                onCaseDocUploaded={handleOnSurgeonPreOpDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
            {surgeonSubTab === 'intraOp' &&
              <CaseDoc
                key='surgeon_intra_op_docs'
                showFlag
                formType={FORM_TYPE.surgeonIntraOp}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.surgeonIntraOpDocs}
                isNone={caseData.isNoneSurgeonIntraOpDocs}
                onDeleteDocument={handleOnDeleteSurgeonIntraOpDocs}
                onDeletePage={handleOnDeleteSurgeonIntraOpDocsPage}
                onChangeIsNone={handleOnChangeIsNoneSurgeonIntraOpDocs}
                onCaseDocUploaded={handleOnSurgeonIntraOpDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
            {surgeonSubTab === 'postOp' &&
              <CaseDoc
                key='surgeon_post_op_docs'
                showFlag
                formType={FORM_TYPE.surgeonPostOp}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.surgeonPostOpDocs}
                isNone={caseData.isNoneSurgeonPostOpDocs}
                onDeleteDocument={handleOnDeleteSurgeonPostOpDocs}
                onDeletePage={handleOnDeleteSurgeonPostOpDocsPage}
                onChangeIsNone={handleOnChangeIsNoneSurgeonPostOpDocs}
                onCaseDocUploaded={handleOnSurgeonPostOpDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
          </div>
        </>
      );
      break;
    case TABS[5].text: // Nursing tab
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="nursing_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* PreOp sub tab */}
            <TabItem
              key='8863112'
              tabKey='8831121'
              parentKey='8831122'
              text='PreOp'
              isActive={nursingSubTab === 'preOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (caseData.isNoneNursingPreOpDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (Array.isArray(caseData.nursingPreOpDocs) && caseData.nursingPreOpDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '90px',
                minWidth: '90px',
                maxWidth: '90px',
                background: '#0050A0',
                borderBottom: nursingSubTab === 'preOp' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setNursingSubTab('preOp');
              }}
            >
              PreOp
            </TabItem>
            {/* IntraOp sub tab */}
            <TabItem
              key='99663113'
              tabKey='996631131'
              parentKey='996631132'
              text='IntraOp'
              isActive={nursingSubTab === 'intraOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (caseData.isNoneNursingIntraOpDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (Array.isArray(caseData.nursingIntraOpDocs) && caseData.nursingIntraOpDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '90px',
                minWidth: '90px',
                maxWidth: '90px',
                background: '#0050A0',
                borderLeft: '1px solid black',
                borderBottom: nursingSubTab === 'intraOp' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setNursingSubTab('intraOp');
              }}
            >
              IntraOp
            </TabItem>
            {/* PostOp sub tab */}
            <TabItem
              key='77663114'
              tabKey='776631143'
              parentKey='776631434'
              text='PostOp'
              isActive={nursingSubTab === 'postOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                if (caseData.isNoneNursingPostOpDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (Array.isArray(caseData.nursingPostOpDocs) && caseData.nursingPostOpDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '90px',
                minWidth: '90px',
                maxWidth: '90px',
                background: '#0050A0',
                borderLeft: '1px solid black',
                borderBottom: nursingSubTab === 'postOp' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setNursingSubTab('postOp');
              }}
            >
              PostOp
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {nursingSubTab === 'preOp' &&
              <CaseDoc
                key='nursing_pre_op_docs'
                showFlag
                formType={FORM_TYPE.nursingPreOp}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.nursingPreOpDocs}
                isNone={caseData.isNoneNursingPreOpDocs}
                onDeleteDocument={handleOnDeleteNursingPreOpDocs}
                onDeletePage={handleOnDeleteNursingPreOpDocsPage}
                onChangeIsNone={handleOnChangeIsNoneNursingPreOpDocs}
                onCaseDocUploaded={handleOnNursingPreOpDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
            {nursingSubTab === 'intraOp' &&
              <CaseDoc
                key='nursing_intra_op_docs'
                showFlag
                formType={FORM_TYPE.nursingIntraOp}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.nursingIntraOpDocs}
                isNone={caseData.isNoneNursingIntraOpDocs}
                onDeleteDocument={handleOnDeleteNursingIntraOpDocs}
                onDeletePage={handleOnDeleteNursingIntraOpDocsPage}
                onChangeIsNone={handleOnChangeIsNoneNursingIntraOpDocs}
                onCaseDocUploaded={handleOnNursingIntraOpDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
            {nursingSubTab === 'postOp' &&
              <CaseDoc
                key='nursing_post_op_docs'
                showFlag
                formType={FORM_TYPE.nursingPostOp}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.nursingPostOpDocs}
                isNone={caseData.isNoneNursingPostOpDocs}
                onDeleteDocument={handleOnDeleteNursingPostOpDocs}
                onDeletePage={handleOnDeleteNursingPostOpDocsPage}
                onChangeIsNone={handleOnChangeIsNoneNursingPostOpDocs}
                onCaseDocUploaded={handleOnNursingPostOpDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
          </div>
        </>
      );
      break;
    case TABS[6].text: // Orders tab
      activeDocComponent = (
        <>
          <TabsContainer
            dropDownId="orders_forms_sub_tabs"
            showPlusIcon={false}
            isDrawer={false}
            shiftMethod='minWidth'
            minWidth={610}
            containerStyles={{ width: '100%' }}
          >
            {/* Orders - PreOp Tab */}
            <TabItem
              key='2000334'
              tabKey='2000334'
              parentKey='2000334'
              text='PreOp'
              isActive={ordersSubTab === 'preOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                const {
                  isNoneOrdersPreOpDocs = false,
                  ordersPreOpDocs = []
                } = caseData;
                if (isNoneOrdersPreOpDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (Array.isArray(ordersPreOpDocs) && ordersPreOpDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                // width: '110px',
                // minWidth: '110px',
                // maxWidth: '110px',
                // background: ordersSubTab === 'preOp' ? '#184878' : '#083050'
                width: '90px',
                minWidth: '90px',
                maxWidth: '90px',
                background: '#0050A0',
                borderBottom: ordersSubTab === 'preOp' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setOrdersSubTab('preOp');
              }}
            >
              PreOp
            </TabItem>
            {/* IntraOp Tab */}
            <TabItem
              key='2001334'
              tabKey='2001334'
              parentKey='3000334'
              text='IntraOp'
              isActive={ordersSubTab === 'intraOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                const {
                  isNoneOrdersIntraOpDocs = false,
                  ordersIntraOpDocs = []
                } = caseData;
                if (isNoneOrdersIntraOpDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (Array.isArray(ordersIntraOpDocs) && ordersIntraOpDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                // width: '110px',
                // minWidth: '110px',
                // maxWidth: '110px',
                // background: ordersSubTab === 'intraOp' ? '#184878' : '#083050',\
                width: '90px',
                minWidth: '90px',
                maxWidth: '90px',
                background: '#0050A0',
                borderLeft: '1px solid black',
                borderBottom: ordersSubTab === 'intraOp' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setOrdersSubTab('intraOp');
              }}
            >
              IntraOp
            </TabItem>
            {/* PostOp */}
            <TabItem
              key='2002'
              tabKey='2002'
              parentKey='4000'
              text='PostOp'
              isActive={ordersSubTab === 'postOp'}
              showTabIcon
              showSvgIcon
              svgIcon={(() => {
                const {
                  isNoneOrdersPostOpDocs = false,
                  ordresPostOpDocs
                } = caseData;
                if (isNoneOrdersPostOpDocs) {
                  return <DocumentNoneIcon height={20} />;
                }
                if (Array.isArray(ordresPostOpDocs) && ordresPostOpDocs.length > 0) {
                  return <DocumentSuccessIcon height={20} />;
                } else {
                  return <DocumentCriticalIcon height={20} />;
                }
              })()}
              style={{
                ...COMMON_TAB_ITEM_STYLES,
                width: '110px',
                minWidth: '110px',
                maxWidth: '110px',
                // background: ordersSubTab === 'postOp' ? '#184878' : '#083050',
                background: '#0050A0',
                borderLeft: '1px solid black',
                borderBottom: ordersSubTab === 'postOp' ? `2px solid ${blue[500]}` : 'none'
              }}
              textStyles={TABS_TEXT_STYLES}
              onActivate={() => {
                setOrdersSubTab('postOp');
              }}
            >
              PostOp
            </TabItem>
          </TabsContainer>
          <div className={css(styles.pdfDocContainer)}>
            {ordersSubTab === 'preOp' &&
              <CaseDoc
                key='orders_preop_docs'
                showFlag
                formType={FORM_TYPE.ordersPreOp}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.ordersPreOpDocs}
                isNone={caseData.isNoneOrdersPreOpDocs}
                onDeleteDocument={handleOnDeleteOrdersPreOpDocs}
                onDeletePage={handleOnDeleteOrdersPreOpDocsPage}
                onChangeIsNone={handleOnChangeIsNoneOrdersPreOpDocs}
                onCaseDocUploaded={handleOnOrdersPreOpDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
            {ordersSubTab === 'intraOp' &&
              <CaseDoc
                key='orders_intraop_docs'
                showFlag
                formType={FORM_TYPE.ordersIntraOp}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.ordersIntraOpDocs}
                isNone={caseData.isNoneOrdersIntraOpDocs}
                onDeleteDocument={handleOnDeleteOrdersIntraOpDocs}
                onDeletePage={handleOnDeleteOrdersIntraOpDocsPage}
                onChangeIsNone={handleOnChangeIsNoneOrdersIntraOpDocs}
                onCaseDocUploaded={handleOnOrdersIntraOpDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
            {ordersSubTab === 'postOp' &&
              <CaseDoc
                key='orders_intraop_docs'
                showFlag
                formType={FORM_TYPE.ordersPostOp}
                flagType={FLAG_VALUE.critical}
                scheduleId={caseData._id}
                caseDocs={caseData.ordresPostOpDocs}
                isNone={caseData.isNoneOrdersPostOpDocs}
                onDeleteDocument={handleOnDeleteOrdersPostOpDocs}
                onDeletePage={handleOnDeleteOrdersPostOpDocsPage}
                onChangeIsNone={handleOnChangeIsNoneOrdersPostOpDocs}
                onCaseDocUploaded={handleOnOrdersPostOpDocsUploaded}
                onEdit={handleOnEditDocs}
                onScanDocument={handleOnScanDocs}
                onShowCropper={handleOnShowCropper}
                onShowCamera={handleOnShowCamera}
                onShowDocWriter={handleOnShowDocWriter}
              />
            }
          </div>
        </>
      );
      break;
    case TABS[7].text: // Discharge tab
      activeDocComponent = (
        <CaseDoc
          key='discharge_docs'
          showFlag
          formType={FORM_TYPE.discharge}
          flagType={FLAG_VALUE.critical}
          scheduleId={caseData._id}
          caseDocs={caseData.dischargeDocs}
          isNone={caseData.isNoneDischargeDocs}
          onDeleteDocument={handleOnDeleteDischargeDocs}
          onDeletePage={handleOnDeleteDischargeDocsPage}
          onChangeIsNone={handleOnChangeIsNoneDischargeDocs}
          onCaseDocUploaded={handleOnDischargeDocsUploaded}
          onEdit={handleOnEditDocs}
          onScanDocument={handleOnScanDocs}
          onShowCropper={handleOnShowCropper}
          onShowCamera={handleOnShowCamera}
          onShowDocWriter={handleOnShowDocWriter}
        />
      );
      break;
    default:
      activeDocComponent = null;
  }

  // flagType={findFlag(props.caseTabFlag, patientFlag, payerFlag)}
  console.log(`props.caseTabFlag = ${props.caseTabFlag}`);
  console.log(`patientFlag = ${patientFlag}`);
  console.log(`payerFlag = ${payerFlag}`);

  return (
    <div className={css(styles.scheduleDashboardContainer)}>
      {/* ----- Network ops loader */}
      {showLoader &&
        <Loader
          iconType='cog'
          iconSize='5x'
          text={loaderMsg}
          style={{ opacity: '0.85' }}
        />
      }

      {/* Prv CameraDrawer */}
      {showPrvCamDrawer &&
        <CameraDrawer
          caseDocs={scannedDocs.caseDocs}
          onClose={handleOnClosePrvCameraDrawer}
          onScan={handleOnScanPrvCameraDrawer}
        />
      }

      {/* Prv CamInspectorDrawer */}
      {showPrvCamInspectorDrawer &&
        <CamInspectorDrawer
          newImageUrl={scannedDocs.newImageUrl}
          caseDocs={scannedDocs.caseDocs}
          selectedDocument={scannedDocs.selectedPageId}
          onClickCancel={handleOnCancelPrvScanDocs}
          onRetake={handleOnRetakePrvScanDocs}
          onClose={handleOnClosePrvScanDocs}
        />
      }

      {/* Scan document (Camera drawer) */}
      {showCameraDrawer &&
        <CameraDrawer
          caseDocs={scanDocumentData.caseDocs}
          onClose={handleOnCloseCameraDrawer}
          onScan={handleOnScanCameraDrawer}
        />
      }

      {/* Cam inspector drawer */}
      {showCamInspectorDrawer &&
        <CamInspectorDrawer
          newImageUrl={scanDocumentData.newImageUrl}
          caseDocs={scanDocumentData.caseDocs}
          selectedDocument={scanDocumentData.selectedPageId}
          onClickCancel={handleOnCancelScanDocs}
          onRetake={handleOnRetakeScanDocs}
          onClose={handleOnCloseScanDocs}
        />
      }

      {/* Cropper Drawer */}
      {showCropperDrawer &&
        <CropperDrawer
          isNewCase
          formType={cropperDocs.formType}
          caseDocs={cropperDocs.caseDocs}
          activeDocId={cropperDocs.selectedPageId}
          onClose={handleOnCropperClose}
        />
      }

      {/* ----- DocWriter drawer ----- */}
      {showDocWriter &&
        <DocWriterDrawer
          formType={docWriterData.formType}
          formName={docWriterData.formName}
          scheduleId={docWriterData.scheduleId}
          activeDocId={docWriterData.currentPageId}
          caseDocs={docWriterData.caseDocuments}
          drawings={[]}
          onClose={handleOnCloseDocWriter}
          onCloseWithoutSave={handleOnCancelDocWriter}
          onPageSelected={handleOnDocWriterPageSelected}
        />
      }

      {/* ----- Dashboard header */}
      <div className={css(styles.dashboardHeader)}>
        {/* Left side */}
        <div className='fx fx__hv-center'>
          <div className={`pl-1 ${css(styles.dashboardHeaderTitle)}`}>
            {caseData.facility && caseData.facility.icon &&
              <img
                className='mr-2'
                src={caseData.facility.icon}
                style={{
                  maxHeight: '32px'
                }}
              />
            }
            {caseData.facility && caseData.facility.facilityName
              ? caseData.facility.facilityName
              : 'Faciity Name N/A'
            }
          </div>
        </div>
        {/* Right side - (Collapse Icon) */}
        <div className='fx fx__hv-center'>
          {/* Schedule Master Flag */}
          {/* {scheduleMasterFlag === FLAG_VALUE.success && SUCCESS_ICON} */}
          {/* {scheduleMasterFlag === FLAG_VALUE.warning && BELL_ICON} */}
          {/* {scheduleMasterFlag === FLAG_VALUE.critical && CRITIAL_ICON} */}
          {/* Header Popup Menu */}
          <div
            className={`ml-3 ${css(styles.headerEllipsisBtn)}`}
            ref={headerMenuRef}
            style={{ display: 'none' }}
          >
            <i
              className='fa fa-ellipsis-h'
              onClick={() => {
                setShowHeaderMenu(true);
              }}
            />
            {showHeaderMenu &&
              <PopupMenu
                top={32}
                left={0}
                zIndex='1550'
                position="absolute"
                visible={showHeaderMenu}
                parent={headerMenuRef}
                onOutsideClick={() => { setShowHeaderMenu(false); }}
              >
                <div
                  className={css(styles.searchPopupMenuContainer)}
                  style={{
                    width: '140px'
                  }}
                >
                  {/* Save Button */}
                  <div
                    className={`pl-2 fx fx__v-center ${css(styles.menuItem)}`}
                    style={{ height: '32px' }}
                    onClick={() => {
                      setShowHeaderMenu(false);
                      props.saveCaseSchedule({
                        ...props.activeCaseSchedule,
                        caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
                        patientId: apd._id || undefined
                      });
                    }}
                  >
                    Save
                  </div>
                  {/* Save and Close */}
                  <div
                    className={`pl-2 fx fx__v-center ${css(styles.menuItem)}`}
                    style={{ height: '32px' }}
                    onClick={() => {
                      const loadGanttGrid = JSON.parse(localStorage.getItem('loadGanttGrid'));
                      setShowHeaderMenu(false);
                      props.saveCaseSchedule({
                        ...props.activeCaseSchedule,
                        caseFlag: findFlag(props.caseTabFlag, patientFlag, payerFlag),
                        patientId: apd._id
                      });
                      props.collapseReload();
                      if (loadGanttGrid) {
                        localStorage.setItem("loadGanttGrid", JSON.stringify(false));
                        history.push('/schedule/gantt');
                      } else {
                        history.push('/schedule');
                      }
                    }}
                  >
                    Save &amp; Close
                  </div>
                </div>
              </PopupMenu>
            }
          </div>
          {/* Collapse Icon - (was Disabled 07/04/23) */}
          <div
            className='ml-2 mr-2' style={{ height: '24px' }}
            onClick={() => {
              setShowLoader(true);
              props.clearCaseSchedulesData();
              const loadGanttGrid = JSON.parse(localStorage.getItem('loadGanttGrid'));
              setShowHeaderMenu(false);
              const tDataFlag = calculateCaseScheduleDataFlag(props.activeCaseSchedule)
              const tFlag = findFlag(tDataFlag, patientFlag, payerFlag);
              console.log(`Flag value = ${tFlag}`);
              const reqHeaders = new Headers();
              reqHeaders.append(
                "Authorization",
                `Bearer ${localStorage.getItem("token")}`,
              );

              const formData = new FormData();
              // Sending case data as a file to bypass http request pararms size limit
              const cData = {
                ...props.activeCaseSchedule,
                caseFlag: tFlag,
                patientId: apd._id
              };
              formData.append('json', new Blob([JSON.stringify(cData)], { type: 'application/json' }));

              const requestOptions = {
                method: "POST",
                headers: reqHeaders,
                body: formData,
                redirect: "follow",
              };

              fetch(`${BASE_URL}/casesSchedule/updateCaseSchedule`, requestOptions)
                .then((response) => response.text())
                .then((result) => {
                  setShowLoader(false);
                  props.collapseReload();
                  if (loadGanttGrid) {
                    localStorage.setItem("loadGanttGrid", JSON.stringify(false));
                    history.push('/schedule/gantt');
                  } else {
                    history.push('/schedule');
                  }
                })
                .catch((err) => {
                  setShowLoader(false);
                  debugLogToConsole(err);
                });
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
            >
              <path
                fill="#96C8FF"
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.926 0h20.145C23.136 0 23.997.861 24 1.926v20.148A1.925 1.925 0 0 1 22.074 24H1.926A1.925 1.925 0 0 1 0 22.074V1.926C0 .861.861 0 1.926 0Zm11.549 11.14h5.738a.786.786 0 1 0 0-1.57H15.44l4.149-4.144a.784.784 0 0 0-.556-1.34.783.783 0 0 0-.556.23l-4.214 4.209v-3.74a.786.786 0 0 0-1.573 0v5.572a.783.783 0 0 0 .785.784ZM4.788 14.43h3.775l-4.15 4.144a.786.786 0 0 0 1.112 1.11l4.216-4.208v3.74a.786.786 0 0 0 1.574 0v-5.572a.786.786 0 0 0-.787-.786H4.789a.786.786 0 0 0-.001 1.572Z"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Dashboard container */}
      <div className={css(styles.dashboardContainer)}>
        {/* Case documents and patient sticker */}
        <div className={css(styles.caseDocsContainer)}>
          {/* Patient Sticker */}
          <div className={css(styles.patientStickerContainer)}>
            <PatientCard
              onClickCase={handleOnClickOpenCaseDataDrawer}
              // flagType={findFlag(patientFlag, payerFlag)}
              flagType={findFlag(props.caseTabFlag, patientFlag, payerFlag)}
              patientData={patientData}
            />
          </div>
          {/* Document tabs */}
          <div className={css(styles.docTabs)}>
            <TabsContainer
              dropDownId=''
              showPlusIcon={false}
              isDrawer={false}
              shiftMethod='minWidth'
              minWidth={610}
              containerStyles={{ width: '100%' }}
              enableScrolling
            >
              {TABS.map((tab) => {
                if (window.innerWidth <= 720) {
                  if (!tab.hideOnMobile) {
                    return (
                      <TabItem
                        key={tab.id}
                        tabKey={tab.id}
                        parentKey={tab.pKey}
                        text={tab.text}
                        isActive={activeTab === tab.text}
                        showTabIcon
                        showSvgIcon
                        svgIcon={(() => {
                          const {
                            isNoneFaceSheet = false,
                            faceSheetDocs = [],
                            isNoneLabDocs = false,
                            isNoneEkgDocs = false,
                            isNoneImagingDocs = false,
                            isNoneConsults = false,
                            labsDocs = [],
                            ekgDocs = [],
                            imagingDocs = [],
                            consultsDocs = [],
                            isNoneConsentSurgFormDocs = false,
                            isNoneConsentFormDocs = false,
                            consentSurgFormDocs = [],
                            consentFormDocs = [],
                            asaScore = '',
                            isNonePreOpFormDocs = false,
                            preOpFormDocs = [],
                            anesthesiaTypes = [],
                            anesthesiaModifiers = [],
                            isNoneIntraOpFormDocs = false,
                            intraOpFormDocs = [],
                            isNonePostOpFormDocs = false,
                            postOpFormDocs = [],
                            isNoneAnesthesiaProcedure = false,
                            anesthesiaProcedures = [],
                            isNoneNursingDocs = false,
                            nursingDocs = [],
                            isNoneSurgeonDocs = false,
                            surgeonDocs = [],
                            isNoneDischargeDocs = false,
                            dischargeDocs = [],
                            isNoneOrdersPreOpDocs = false,
                            isNoneOrdersIntraOpDocs = false,
                            isNoneOrdersPostOpDocs = false,
                            ordersPreOpDocs = [],
                            ordersIntraOpDocs = [],
                            ordresPostOpDocs = []
                          } = caseData;

                          switch (tab.text) {
                            case 'FaceSheet':
                              if (isNoneFaceSheet) {
                                return IS_NONE_ICON;
                              }
                              if (Array.isArray(faceSheetDocs) && (faceSheetDocs.length > 0 || isNoneFaceSheet)) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            case 'Records': {
                              if (isNoneLabDocs && isNoneEkgDocs && isNoneImagingDocs && isNoneConsults) {
                                return IS_NONE_ICON;
                              }

                              const labsFlag = labsDocs.length > 0 || isNoneLabDocs;
                              const ekgFlag = ekgDocs.length > 0 || isNoneEkgDocs;
                              const imagingFlag = imagingDocs.length > 0 || isNoneImagingDocs;
                              const consultsFlag = consultsDocs.length > 0 || isNoneConsults;

                              if (labsFlag && ekgFlag && imagingFlag && consultsFlag) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            }
                            case 'Consents': {
                              if (isNoneConsentSurgFormDocs && isNoneConsentFormDocs) {
                                return IS_NONE_ICON;
                              }

                              const surgFlag = consentSurgFormDocs.length > 0 || isNoneConsentSurgFormDocs;
                              const anesFlag = consentFormDocs.length > 0 || isNoneConsentFormDocs;

                              if (surgFlag && anesFlag) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            }
                            case 'Anesthesia': {
                              // PreOp Tab Flag
                              let preOpFlagValue = FLAG_VALUE.critical;
                              let anesthesiaAesFlagValue = FLAG_VALUE.critical;
                              if (asaScore.length > 0) {
                                anesthesiaAesFlagValue = FLAG_VALUE.success;
                              }
                              if (anesthesiaAesFlagValue === FLAG_VALUE.critical) {
                                preOpFlagValue = anesthesiaAesFlagValue;
                              } else {
                                if (isNonePreOpFormDocs) {
                                  preOpFlagValue = FLAG_VALUE.none;
                                } else {
                                  if (preOpFormDocs.length > 0) {
                                    preOpFlagValue = FLAG_VALUE.success;
                                  } else {
                                    preOpFlagValue = FLAG_VALUE.critical;
                                  }
                                }
                              }

                              // IntraOp Tab Flag
                              let intraOpFlagValue = FLAG_VALUE.critical;
                              let anesthesiaDataFlagValue = FLAG_VALUE.critical;
                              if (anesthesiaTypes.length > 0 && anesthesiaModifiers.length > 0) {
                                anesthesiaDataFlagValue = FLAG_VALUE.success;
                              }
                              if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
                                intraOpFlagValue = anesthesiaDataFlagValue;
                              } else {
                                if (isNoneIntraOpFormDocs) {
                                  intraOpFlagValue = FLAG_VALUE.none;
                                } else {
                                  if (intraOpFormDocs.length > 0) {
                                    intraOpFlagValue = FLAG_VALUE.success;
                                  } else {
                                    intraOpFlagValue = FLAG_VALUE.critical;
                                  }
                                }
                              }

                              // PostOp Tab Flag
                              let postOpFlagValue = FLAG_VALUE.critical;
                              if (isNonePostOpFormDocs) {
                                postOpFlagValue = FLAG_VALUE.none;
                              } else {
                                if (postOpFormDocs.length > 0) {
                                  postOpFlagValue = FLAG_VALUE.success;
                                } else {
                                  postOpFlagValue = FLAG_VALUE.critical;
                                }
                              }

                              // Procedures Tab Flag
                              let proceduresFlagValue = FLAG_VALUE.critical;
                              if (isNoneAnesthesiaProcedure) {
                                proceduresFlagValue = FLAG_VALUE.none;
                              } else {
                                if (anesthesiaProcedures.length === 0) {
                                  proceduresFlagValue = FLAG_VALUE.critical;
                                } else if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
                                  proceduresFlagValue = FLAG_VALUE.warning;
                                } else if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length === 0) {
                                  proceduresFlagValue = FLAG_VALUE.success;
                                } else {
                                  proceduresFlagValue = FLAG_VALUE.critical;
                                }
                              }

                              const anesthesiaTabFlag = findFlag(preOpFlagValue, intraOpFlagValue, postOpFlagValue, proceduresFlagValue);
                              switch (anesthesiaTabFlag) {
                                case FLAG_VALUE.critical:
                                  return CRITIAL_ICON;
                                case FLAG_VALUE.warning:
                                  return BELL_ICON;
                                case FLAG_VALUE.success:
                                  return SUCCESS_ICON;
                                case FLAG_VALUE.none:
                                  return IS_NONE_ICON;
                                default:
                                  return CRITIAL_ICON;
                              }
                            }
                            case 'Nursing':
                              if (isNoneNursingDocs) {
                                return IS_NONE_ICON;
                              }
                              if (nursingDocs.length > 0 || isNoneNursingDocs) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            case 'Surgeon':
                              if (isNoneSurgeonDocs) {
                                return IS_NONE_ICON;
                              }
                              if (surgeonDocs.length > 0 || isNoneSurgeonDocs) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            case 'Discharge':
                              if (isNoneDischargeDocs) {
                                return IS_NONE_ICON;
                              }
                              if (dischargeDocs.length > 0 || isNoneDischargeDocs) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            case 'Orders': {
                              if (isNoneOrdersPreOpDocs && isNoneOrdersIntraOpDocs && isNoneOrdersPostOpDocs) {
                                return IS_NONE_ICON;
                              }
                              const preOPFlag = ordersPreOpDocs.length > 0 || isNoneOrdersPreOpDocs;
                              const intraOpFlag = ordersIntraOpDocs.length > 0 || isNoneOrdersIntraOpDocs;
                              const postOpFlag = ordresPostOpDocs.length > 0 || isNoneOrdersPostOpDocs;

                              if (preOPFlag && intraOpFlag && postOpFlag) {
                                return SUCCESS_ICON;
                              }
                              return CRITIAL_ICON;
                            }
                            default:
                              return CRITIAL_ICON;
                          }
                        })()}
                        style={{
                          ...tab.styles,
                          background: activeTab === tab.text ? '#184878' : '#083050'
                        }}
                        textStyles={TABS_TEXT_STYLES}
                        onActivate={() => {
                          setActiveTab(tab.text);
                        }}
                      >
                        {!tab.isIconText ? tab.text : tab.textIcon}
                      </TabItem>
                    );
                  }
                }
                return (
                  <TabItem
                    key={tab.id}
                    tabKey={tab.id}
                    parentKey={tab.pKey}
                    text={tab.text}
                    isActive={activeTab === tab.text}
                    showTabIcon
                    showSvgIcon
                    svgIcon={(() => {
                      const {
                        isNoneFaceSheet = false,
                        faceSheetDocs = [],
                        isNoneLabDocs = false,
                        isNoneEkgDocs = false,
                        isNoneImagingDocs = false,
                        isNoneConsults = false,
                        labsDocs = [],
                        ekgDocs = [],
                        imagingDocs = [],
                        consultsDocs = [],
                        isNoneConsentSurgFormDocs = false,
                        isNoneConsentFormDocs = false,
                        consentSurgFormDocs = [],
                        consentFormDocs = [],
                        asaScore = '',
                        isNonePreOpFormDocs = false,
                        preOpFormDocs = [],
                        anesthesiaTypes = [],
                        anesthesiaModifiers = [],
                        isNoneIntraOpFormDocs = false,
                        intraOpFormDocs = [],
                        isNonePostOpFormDocs = false,
                        postOpFormDocs = [],
                        isNoneAnesthesiaProcedure = false,
                        anesthesiaProcedures = [],
                        // isNoneNursingDocs = false,
                        // nursingDocs = [],
                        isNoneNursingPreOpDocs = false,
                        nursingPreOpDocs = [],
                        isNoneNursingIntraOpDocs = false,
                        nursingIntraOpDocs = [],
                        isNoneNursingPostOpDocs = false,
                        nursingPostOpDocs = [],
                        // isNoneSurgeonDocs = false,
                        // surgeonDocs = [],
                        isNoneSurgeonPreOpDocs = false,
                        surgeonPreOpDocs = [],
                        isNoneSurgeonIntraOpDocs = false,
                        surgeonIntraOpDocs = [],
                        isNoneSurgeonPostOpDocs = false,
                        surgeonPostOpDocs = [],
                        isNoneDischargeDocs = false,
                        dischargeDocs = [],
                        isNoneOrdersPreOpDocs = false,
                        isNoneOrdersIntraOpDocs = false,
                        isNoneOrdersPostOpDocs = false,
                        ordersPreOpDocs = [],
                        ordersIntraOpDocs = [],
                        ordresPostOpDocs = []
                      } = caseData;
                      switch (tab.text) {
                        case 'FaceSheet':
                          if (isNoneFaceSheet) {
                            return IS_NONE_ICON;
                          }
                          if (faceSheetDocs.length > 0 || isNoneFaceSheet) {
                            return SUCCESS_ICON;
                          }
                          return CRITIAL_ICON;
                        case 'Results': {
                          if (isNoneLabDocs && isNoneEkgDocs && isNoneImagingDocs && isNoneConsults) {
                            return IS_NONE_ICON;
                          }
                          // Labs Flag
                          let labsFlag = FLAG_VALUE.critical;
                          if (isNoneLabDocs) {
                            labsFlag = FLAG_VALUE.none;
                          } else {
                            labsFlag = labsDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
                          }
                          // EKG Flag
                          let ekgFlag = FLAG_VALUE.critical;
                          if (isNoneEkgDocs) {
                            ekgFlag = FLAG_VALUE.none;
                          } else {
                            ekgFlag = ekgDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
                          }
                          // Imaging Flag
                          let imagingFlag = FLAG_VALUE.critical;
                          if (isNoneImagingDocs) {
                            imagingFlag = FLAG_VALUE.none;
                          } else {
                            imagingFlag = imagingDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
                          }
                          // Consults Flag
                          let consultsFlag = FLAG_VALUE.critical;
                          if (isNoneConsults) {
                            consultsFlag = FLAG_VALUE.none;
                          } else {
                            consultsFlag = consultsDocs.length > 0 ? FLAG_VALUE.success : FLAG_VALUE.critical;
                          }

                          const resultsFlag = findFlag(labsFlag, ekgFlag, imagingFlag, consultsFlag);
                          switch (resultsFlag) {
                            case FLAG_VALUE.none:
                              return IS_NONE_ICON;
                            case FLAG_VALUE.success:
                              return SUCCESS_ICON;
                            case FLAG_VALUE.warning:
                              return BELL_ICON;
                            case FLAG_VALUE.critical:
                            default:
                              return CRITIAL_ICON;
                          }
                        }
                        case 'Consents': {
                          if (isNoneConsentSurgFormDocs && isNoneConsentFormDocs) {
                            return IS_NONE_ICON;
                          }

                          const surgFlag = consentSurgFormDocs.length > 0 || isNoneConsentSurgFormDocs;
                          const anesFlag = consentFormDocs.length > 0 || isNoneConsentFormDocs;

                          if (surgFlag && anesFlag) {
                            return SUCCESS_ICON;
                          }
                          return CRITIAL_ICON;
                        }
                        case 'Anesthesia': {
                          // PreOp Tab Flag
                          let preOpFlagValue = FLAG_VALUE.critical;
                          let anesthesiaAesFlagValue = FLAG_VALUE.critical;
                          if (asaScore.length > 0) {
                            anesthesiaAesFlagValue = FLAG_VALUE.success;
                          }
                          if (anesthesiaAesFlagValue === FLAG_VALUE.critical) {
                            preOpFlagValue = anesthesiaAesFlagValue;
                          } else {
                            if (isNonePreOpFormDocs) {
                              // preOpFlagValue = FLAG_VALUE.none;
                              preOpFlagValue = FLAG_VALUE.success;
                            } else {
                              if (preOpFormDocs.length > 0) {
                                preOpFlagValue = FLAG_VALUE.success;
                              } else {
                                preOpFlagValue = FLAG_VALUE.critical;
                              }
                            }
                          }

                          // IntraOp Tab Flag
                          let intraOpFlagValue = FLAG_VALUE.critical;
                          let anesthesiaDataFlagValue = FLAG_VALUE.critical;
                          if (anesthesiaTypes.length > 0 && anesthesiaModifiers.length > 0) {
                            anesthesiaDataFlagValue = FLAG_VALUE.success;
                          }
                          if (anesthesiaDataFlagValue === FLAG_VALUE.critical) {
                            intraOpFlagValue = anesthesiaDataFlagValue;
                          } else {
                            if (isNoneIntraOpFormDocs) {
                              // intraOpFlagValue = FLAG_VALUE.none;
                              intraOpFlagValue = FLAG_VALUE.success;
                            } else {
                              if (intraOpFormDocs.length > 0) {
                                intraOpFlagValue = FLAG_VALUE.success;
                              } else {
                                intraOpFlagValue = FLAG_VALUE.critical;
                              }
                            }
                          }

                          // PostOp Tab Flag
                          let postOpFlagValue = FLAG_VALUE.critical;
                          if (isNonePostOpFormDocs) {
                            // postOpFlagValue = FLAG_VALUE.none;
                            postOpFlagValue = FLAG_VALUE.success;
                          } else {
                            if (postOpFormDocs.length > 0) {
                              postOpFlagValue = FLAG_VALUE.success;
                            } else {
                              postOpFlagValue = FLAG_VALUE.critical;
                            }
                          }

                          // Procedures Tab Flag
                          let proceduresFlagValue = FLAG_VALUE.critical;
                          if (isNoneAnesthesiaProcedure) {
                            proceduresFlagValue = FLAG_VALUE.none;
                          } else {
                            if (anesthesiaProcedures.length === 0) {
                              proceduresFlagValue = FLAG_VALUE.critical;
                            } else if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length > 0) {
                              proceduresFlagValue = FLAG_VALUE.warning;
                            } else if (anesthesiaProcedures.length > 0 && (anesthesiaProcedures.filter(el => el.npi === 0)).length === 0) {
                              proceduresFlagValue = FLAG_VALUE.success;
                            } else {
                              proceduresFlagValue = FLAG_VALUE.critical;
                            }
                          }

                          console.log(`▶️ preOpFlagValue = ${preOpFlagValue}`);
                          console.log(`▶️ preOpFlagValue = ${intraOpFlagValue}`);
                          console.log(`▶️ preOpFlagValue = ${postOpFlagValue}`);
                          console.log(`▶️ preOpFlagValue = ${proceduresFlagValue}`);
                          const anesthesiaTabFlag = findFlag(preOpFlagValue, intraOpFlagValue, postOpFlagValue, proceduresFlagValue);
                          switch (anesthesiaTabFlag) {
                            case FLAG_VALUE.critical:
                              return CRITIAL_ICON;
                            case FLAG_VALUE.warning:
                              return BELL_ICON;
                            case FLAG_VALUE.success:
                              return SUCCESS_ICON;
                            case FLAG_VALUE.none:
                              return IS_NONE_ICON;
                            default:
                              return CRITIAL_ICON;
                          }
                        }
                        case 'Nursing': {
                          // Nursing preop flag
                          let nursingPreOpFlag = FLAG_VALUE.critical;
                          if (isNoneNursingPreOpDocs) {
                            nursingPreOpFlag = FLAG_VALUE.none;
                          } else {
                            nursingPreOpFlag = nursingPreOpDocs.length > 0
                              ? FLAG_VALUE.success
                              : FLAG_VALUE.critical
                          }

                          // Nursing intraop flag
                          let nursingIntraOpFlag = FLAG_VALUE.critical;
                          if (isNoneNursingIntraOpDocs) {
                            nursingIntraOpFlag = FLAG_VALUE.none;
                          } else {
                            nursingIntraOpFlag = nursingIntraOpDocs.length > 0
                              ? FLAG_VALUE.success
                              : FLAG_VALUE.critical
                          }

                          // Nursing preop flag
                          let nursingPostOpFlag = FLAG_VALUE.critical;
                          if (isNoneNursingPostOpDocs) {
                            nursingPostOpFlag = FLAG_VALUE.none;
                          } else {
                            nursingPostOpFlag = nursingPostOpDocs.length > 0
                              ? FLAG_VALUE.success
                              : FLAG_VALUE.critical
                          }
                          const nursingFlag = findFlag(nursingPreOpFlag, nursingIntraOpFlag, nursingPostOpFlag);
                          switch (nursingFlag) {
                            case FLAG_VALUE.critical:
                              return CRITIAL_ICON;
                            case FLAG_VALUE.warning:
                              return BELL_ICON;
                            case FLAG_VALUE.success:
                              return SUCCESS_ICON;
                            case FLAG_VALUE.none:
                              return IS_NONE_ICON;
                            default:
                              return CRITIAL_ICON;
                          }
                        }
                        case 'Surgeon': {
                          // Surgeon preop flag
                          let surgeonPreOpFlag = FLAG_VALUE.critical;
                          if (isNoneSurgeonPreOpDocs) {
                            surgeonPreOpFlag = FLAG_VALUE.none;
                          } else {
                            surgeonPreOpFlag = surgeonPreOpDocs.length > 0
                              ? FLAG_VALUE.success
                              : FLAG_VALUE.critical
                          }
                          // Surgeon intraop flag
                          let surgeonIntraOpFlag = FLAG_VALUE.critical;
                          if (isNoneSurgeonIntraOpDocs) {
                            surgeonIntraOpFlag = FLAG_VALUE.none;
                          } else {
                            surgeonIntraOpFlag = surgeonIntraOpDocs.length > 0
                              ? FLAG_VALUE.success
                              : FLAG_VALUE.critical
                          }
                          // Surgeon postop flag
                          let surgeonPostOpFlag = FLAG_VALUE.critical;
                          if (isNoneSurgeonPostOpDocs) {
                            surgeonPostOpFlag = FLAG_VALUE.none;
                          } else {
                            surgeonPostOpFlag = surgeonPostOpDocs.length > 0
                              ? FLAG_VALUE.success
                              : FLAG_VALUE.critical
                          }

                          const surgeonFlag = findFlag(surgeonPreOpFlag, surgeonIntraOpFlag, surgeonPostOpFlag);
                          switch (surgeonFlag) {
                            case FLAG_VALUE.critical:
                              return CRITIAL_ICON;
                            case FLAG_VALUE.warning:
                              return BELL_ICON;
                            case FLAG_VALUE.success:
                              return SUCCESS_ICON;
                            case FLAG_VALUE.none:
                              return IS_NONE_ICON;
                            default:
                              return CRITIAL_ICON;
                          }
                        }
                        case 'Discharge':
                          if (isNoneDischargeDocs) {
                            return IS_NONE_ICON;
                          }
                          if (dischargeDocs.length > 0 || isNoneDischargeDocs) {
                            return SUCCESS_ICON;
                          }
                          return CRITIAL_ICON;
                        case 'Orders': {
                          if (isNoneOrdersPreOpDocs && isNoneOrdersIntraOpDocs && isNoneOrdersPostOpDocs) {
                            return IS_NONE_ICON;
                          }
                          const preOPFlag = ordersPreOpDocs.length > 0 || isNoneOrdersPreOpDocs;
                          const intraOpFlag = ordersIntraOpDocs.length > 0 || isNoneOrdersIntraOpDocs;
                          const postOpFlag = ordresPostOpDocs.length > 0 || isNoneOrdersPostOpDocs;

                          if (preOPFlag && intraOpFlag && postOpFlag) {
                            return SUCCESS_ICON;
                          }
                          return CRITIAL_ICON;
                        }
                        default:
                          return CRITIAL_ICON;
                      }
                    })()}
                    style={{
                      ...tab.styles,
                      // background: activeTab === tab.text ? '#184878' : '#083050'
                      background: activeTab === tab.text ? '#0050A0' : blue[800]
                    }}
                    textStyles={TABS_TEXT_STYLES}
                    onActivate={() => {
                      setActiveTab(tab.text);
                    }}
                  >
                    {!tab.isIconText ? tab.text : tab.textIcon}
                  </TabItem>
                );
              })}
            </TabsContainer>
          </div>
          {/* Active Tab */}
          <div className={css(styles.docContainer)}>
            {activeDocComponent}
          </div>
        </div>
        {/* Case data drawer (Case, patient and payer tabs) */}
        {showCaseDataDrawer &&
          <div
            className={css(styles.caseDataDrawerContainer,
              isMobileLayout && styles.caseDataDrawerContainerMobile,
              !showCaseDataDrawer && styles.caseDataDrawerContainerClosed
            )}
          >
            {/* Header */}
            <div className={css(styles.caseDataDrawerHeader)}>
              {/* Header text */}
              <div className={css(styles.caseDataDrawerHeaderTitle)}>
                {caseDataDrawerMasterFlag === FLAG_VALUE.success && <CaseSuccessIcon height={18} />}
                {caseDataDrawerMasterFlag === FLAG_VALUE.warning && <CaseWarningIcon height={18} />}
                {caseDataDrawerMasterFlag === FLAG_VALUE.critical && <CaseCriticalIcon height={18} />}
                <span className='ml-2'>
                  Case Posting
                </span>
              </div>
              {/* Close icon */}
              <div className={css(styles.caseDataDrawerHeaderCloseIcon)}>
                <i
                  className='fa fa-times-circle'
                  onClick={handleOnCloseCaseDataDrawer}
                />
              </div>
            </div>
            {/* Case data draswer tabs */}
            <div className={css(styles.caseDataDrawerTabs)}>
              <TabsContainer
                dropDownId="new_case_post_drawer_tabs"
                showPlusIcon={false}
                isDrawer={false}
                shiftMethod='minWidth'
                minWidth={610}
                containerStyles={{ width: '100%' }}
              >
                {/* Case tab */}
                <TabItem
                  key='1211'
                  tabKey='1211'
                  parentKey='1200'
                  text='Case'
                  isActive={caseDataDrawerActiveTab === 'case'}
                  showTabIcon
                  showSvgIcon
                  svgIcon={(() => {
                    switch (props.caseTabFlag) {
                      case FLAG_VALUE.success:
                        return <CaseSuccessIcon height={18} />;
                      case FLAG_VALUE.warning:
                        return <CaseWarningIcon height={18} />;
                      case FLAG_VALUE.critical:
                      default:
                        return <CaseCriticalIcon height={18} />;
                    }
                  })()}
                  style={{
                    ...COMMON_TAB_ITEM_STYLES,
                    width: '105px',
                    minWidth: '105px',
                    maxWidth: '105px',
                    background: caseDataDrawerActiveTab === 'case' ? '#184878' : '#083050'
                  }}
                  textStyles={TABS_TEXT_STYLES}
                  onActivate={() => {
                    handleOnActivateCaseDataDrawerTab('case');
                  }}
                >
                  Case
                </TabItem>
                {/* Patient tab */}
                <TabItem
                  key='1212'
                  tabKey='1212'
                  parentKey='1201'
                  text='Patient'
                  isActive={caseDataDrawerActiveTab === 'patient'}
                  showTabIcon
                  showSvgIcon
                  svgIcon={patientFlag === FLAG_VALUE.success
                    ? <CaseSuccessIcon height={18} />
                    : <CaseWarningIcon heigth={18} />
                  }
                  style={{
                    ...COMMON_TAB_ITEM_STYLES,
                    width: '105px',
                    minWidth: '105px',
                    maxWidth: '105px',
                    background: caseDataDrawerActiveTab === 'patient' ? '#184878' : '#083050'
                  }}
                  textStyles={TABS_TEXT_STYLES}
                  onActivate={() => {
                    handleOnActivateCaseDataDrawerTab('patient');
                  }}
                >
                  Patient
                </TabItem>
                {/* Payer tab */}
                <TabItem
                  key='1213'
                  tabKey='1214'
                  parentKey='1301'
                  text='Payers'
                  isActive={caseDataDrawerActiveTab === 'payers'}
                  showTabIcon
                  showSvgIcon
                  svgIcon={payerFlag === FLAG_VALUE.success
                    ? <CaseSuccessIcon height={18} />
                    : <CaseWarningIcon height={18} />
                  }
                  style={{
                    ...COMMON_TAB_ITEM_STYLES,
                    width: '105px',
                    minWidth: '105px',
                    maxWidth: '105px',
                    background: caseDataDrawerActiveTab === 'payers' ? '#184878' : '#083050'
                  }}
                  textStyles={TABS_TEXT_STYLES}
                  onActivate={() => {
                    handleOnActivateCaseDataDrawerTab('payers');
                  }}
                >
                  Payers
                </TabItem>
              </TabsContainer>
            </div>
            {/* Case data drawer active component */}
            <div className={css(styles.caseDataDrawerActiveComponentContainer)}>
              {caseDataDrawerActiveComponent}
            </div>
          </div>
        }
      </div>
    </div>
  );
};

function mapStateToProps({ caseSchedule, user, sharedData }) {
  return {
    ...caseSchedule,
    ...user,
    ...sharedData
  };
}

export default connect(mapStateToProps, {
  clearCaseSchedulesData,
  updateCaseSchedule,
  saveCaseSchedule,
  collapseReload,
  toggleCaseLoader,
  loadCaseSchedule,
  addPatientToExistingCaseSchedule,
  deletePatientFromCaseSchedule,
  reloadExistingCaseSchedulePatientData
})(ScheduleCaseDashboard);
